import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
  ArrowLeft, X, Plus, Download,
  AlertCircle, CheckCircle, Package, Settings, Trash2, Eye, 
  Volume2, VolumeX, HelpCircle, PlayCircle, Layers, TrendingDown, TrendingUp, Wallet, Receipt
} from 'lucide-react';

import jsPDF from 'jspdf'; 
import autoTable from 'jspdf-autotable'; 
import { useSmartGuide } from './useSmartGuide'; 
import Modal from '../../ui/Modal';
import ConfirmModal from '../../ui/ConfirmModal';
import Button from '../../ui/Button';


const API_BASE = 'https://mukadampro.shirpur.store';

// --- CONSTANTS ---
const FABRIC_TYPES = ["N/A", "30s", "40s", "50s", "60s", "80s", "100s", "Other"];
const MATERIALS = ["N/A", "Cotton", "Poplin", "Cambric", "Polyester", "Viscose", "Yarn", "Other"];
const REVENUE_FABRIC_TYPES = ["30s", "40s", "60s", "80s", "100s"];
const REVENUE_MATERIALS = ["Cotton", "Poplin", "Cambric", "Polyester", "Viscose"];

// --- IMAGES (Icons) ---
const IMAGES = {
    yarn: "https://cdn-icons-png.flaticon.com/512/3063/3063823.png", 
    fabric: "https://cdn-icons-png.flaticon.com/512/2823/2823547.png", 
    revenue: "https://cdn-icons-png.flaticon.com/512/2454/2454282.png", 
    expense: "https://cdn-icons-png.flaticon.com/512/1055/1055644.png", 
    profit: "https://cdn-icons-png.flaticon.com/512/3176/3176366.png", 
    pnl: "https://cdn-icons-png.flaticon.com/512/2666/2666505.png" 
};

// --- UTILS ---
const getToday = () => new Date().toISOString().split("T")[0];
const getFirstDayOfMonth = () => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
};

const formatCurrency = (amount) => {
  return "Rs. " + (Number(amount) || 0).toLocaleString('en-IN');
}



const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit", month: "long", year: "numeric"
  });
};

// --- VOICE FORMATTERS (रुपये-पैसे और पॉइंट के लिए) ---
const formatVoiceCurrency = (amt) => {
    const num = Number(amt) || 0;
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    if (paise > 0) return `${rupees} रुपये ${paise} पैसे`;
    return `${rupees} रुपये`; // .0 या .00 होने पर सिर्फ रुपये बोलेगा
};

const formatVoiceNumber = (num) => {
    const n = Number(num) || 0;
    if (Number.isInteger(n) || n % 1 === 0) return Math.floor(n).toString(); // .0 को इग्नोर करेगा
    const parts = n.toString().split('.');
    return `${parts[0]} पॉइंट ${parts[1]}`;
};

function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) { return initialValue; }
  });
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {}
  };
  return [storedValue, setValue];
}

// --- UI COMPONENTS ---
const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 1500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 100005,
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '12px 24px', borderRadius: '50px',
      background: type === 'success' ? 'rgba(16, 185, 129, 0.9)' : 'rgba(239, 68, 68, 0.9)',
      backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)',
      color: 'white', boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      fontSize: '12px', fontWeight: '400', animation: 'fadeInDown 0.3s ease-out'
    }}>
      {type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
      {message}
    </div>
  );
};

const StatusBadge = ({ status, small }) => {
    const finalStatus = status ? status : 'Paid'; 
    const isPaid = finalStatus === 'Paid';
    return (
        <span style={{
            fontSize: small ? '6px' : '9px', 
            fontWeight: '400', 
            padding: small ? '1px 4px' : '3px 8px', 
            borderRadius: '4px',
            background: isPaid ? 'rgba(22, 163, 74, 0.2)' : 'rgba(220, 38, 38, 0.2)',
            color: isPaid ? '#166534' : '#991B1B', 
            border: isPaid ? '1px solid rgba(22, 163, 74, 0.3)' : '1px solid rgba(220, 38, 38, 0.3)',
            textTransform: 'uppercase', 
            display: 'inline-block', 
            marginLeft: '6px',
            lineHeight: '1'
        }}>
            {finalStatus}
        </span>
    );
};

// --- STAT CARD (Premium Workers.js Style) ---
const StatCard = ({ label, value, valueColor, alert, onView, icon, gradient, shadowColor, voiceText, voiceAssist, handleVoiceClick, actionId, isAlreadyOpen, alreadyOpenMsg }) => (
  <div style={{
    background: '#ffffff', 
    borderRadius: '14px', 
    padding: '10px 12px',
    boxShadow: voiceAssist ? '0 0 10px rgba(16, 185, 129, 0.3)' : '0 4px 10px rgba(0,0,0,0.03)', 
    border: alert ? '1px solid rgba(220, 38, 38, 0.4)' : (voiceAssist ? '1px solid rgba(16, 185, 129, 0.6)' : '1px solid #f1f5f9'),
    display: 'flex', alignItems: 'center', gap: '10px',
    cursor: 'pointer', transition: 'all 0.2s ease',
    position: 'relative'
  }} onClick={(e) => handleVoiceClick(e, voiceText, onView, actionId, isAlreadyOpen, alreadyOpenMsg)}>
    
    <div style={{ background: gradient || 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', padding: '8px', borderRadius: '10px', boxShadow: shadowColor ? `0 2px 6px ${shadowColor}` : '0 2px 6px rgba(2, 132, 199, 0.15)', flexShrink: 0 }}>
        {icon}
    </div>

    <div style={{display:'flex', flexDirection:'column', justifyContent:'center', flex:1}}>
        <span style={{ fontSize: '7.5px', color: '#64748B', fontWeight: '400', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
            {label}
        </span>
        <div style={{ fontSize: '15px', fontWeight: '600', color: valueColor || '#0F172A', letterSpacing: '-0.3px', marginTop: '-2px' }}>
            {value}
        </div>
        
        {alert && (
            <div style={{ marginTop:'2px', color:'#DC2626', fontSize:'8px', fontWeight:'400', background:'rgba(254, 242, 242, 0.8)', padding:'2px 6px', borderRadius:'6px', width:'fit-content', display:'flex', alignItems:'center', gap:'4px', border: '1px solid rgba(220, 38, 38, 0.2)' }}>
                <AlertCircle size={8}/> Low Stock
            </div>
        )}

        {onView && (
            <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(241, 245, 249, 0.8)', padding: '4px', borderRadius: '6px' }}>
                <Eye size={12} color="#64748B"/>
            </div>
        )}
    </div>
  </div>
);

const PnLRow = ({ label, amount, isTotal, isSub, color, icon, voiceText, handleVoiceClick }) => {
  // 👇 यहाँ हम कलर को फोर्स करने के लिए क्लास सेट कर रहे हैं 👇
  let colorClass = "";
  if (color === "#16A34A") colorClass = "force-green";
  else if (color === "#DC2626") colorClass = "force-red";

  return (
    <div 
      onClick={(e) => voiceText ? handleVoiceClick(e, voiceText, null) : null}
      className={colorClass}
      style={{
          display: 'flex', justifyContent: 'space-between', padding: '12px 0',
          borderBottom: isTotal ? 'none' : '1px dashed #e2e8f0', alignItems: 'center',
          cursor: voiceText ? 'pointer' : 'default'
    }}>
      <span className={colorClass} style={{
        fontSize: isTotal ? '13px' : '11px', 
        fontWeight: isTotal ? '600' : '400', 
        color: color ? color : (isTotal ? '#0F172A' : (isSub ? '#64748B' : '#334155')),
        paddingLeft: isSub ? '10px' : '0'
      }}>
        {label}
      </span>
      <span className={colorClass} style={{
        fontSize: isTotal ? '14px' : '13px', 
        fontWeight: '600', 
        color: color ? color : '#0F172A',
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px'
      }}>
        {amount}
        {icon && icon}
      </span>
    </div>
  );
};

// --- MAIN INVENTORY COMPONENT ---
const Inventory = ({ onBack, appBg, globalGuide }) => {
  const [activeTab, setActiveTab] = useState('overview'); 
  
  // --- USE CUSTOM HOOK FOR VOICE ---

  const {
        voiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage,
        toggleVoiceAssist, 
        handleFocusVoice, handleSelectVoice, handleTextBlurVoice,
        startSmartGuide, endSmartGuide, advanceNextStep,
        speakHinglish, setPendingConfirm
    } = globalGuide;  // <--- बस इतना सा बदलाव!

  // डबल-क्लिक की मेमोरी और टाइमर के लिए (20 सेकंड का टाइम)
  const clickMemory = useRef({ id: null, time: 0 });

  // --- SMART DOUBLE-CLICK VOICE WRAPPER ---
  const handleVoiceClick = (e, voiceText, actionFunction, actionId, isAlreadyOpen, alreadyOpenMsg) => {
      if (e) { e.preventDefault(); e.stopPropagation(); }

      if (isAlreadyOpen) {
          if (voiceAssist && !activeGuideFlow && alreadyOpenMsg) {
              speakHinglish(alreadyOpenMsg);
          }
          return;
      }

      if (!voiceAssist || activeGuideFlow) {
          if (actionFunction) actionFunction();
          return;
      }

      if (!actionFunction) {
          speakHinglish(voiceText);
          return;
      }

      const uid = actionId || voiceText;
      const now = Date.now();
      const timeDiff = now - clickMemory.current.time;

      if (clickMemory.current.id === uid && timeDiff < 20000) {
          if (actionFunction) actionFunction();
          clickMemory.current = { id: null, time: 0 }; 
      } else {
          // 👇 यहाँ हर एक्शन के हिसाब से सटीक सवाल पूछा जाएगा
          let confirmMsg = voiceText;
          
          if (actionId === 'btn-settings') {
              confirmMsg += " क्या आप सेटिंग्स ओपन करना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'btn-add-exp') {
              confirmMsg += " क्या आप नया खर्चा जोड़ना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'btn-add-rev') {
              confirmMsg += " क्या आप नई आमदनी जोड़ना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'btn-pdf') {
              confirmMsg += " क्या आप Financial Report डाउनलोड करना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'btn-history') {
              confirmMsg += " क्या आप पुरानी Financial Report की हिस्ट्री देखना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'tab-overview') {
              confirmMsg += " क्या आप Financial Report का हिसाब देखना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'tab-ledger') {
              confirmMsg += " क्या आप पार्टी लेजर यानी लेन-देन देखना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'view-exp-history') {
              confirmMsg += " क्या आप Expenses यानी खर्चों की पूरी हिस्ट्री देखना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'view-rev-history') {
              confirmMsg += " क्या आप Revenue यानी आमदनी की पूरी हिस्ट्री देखना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'tab-general-expense') {
              confirmMsg += " क्या आप जनरल खर्चे के Button में जाना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'tab-yarn-expense') {
              confirmMsg += " क्या आप Yarn Purchase यानी धागा खरीदी की एंट्री करना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'view-report') {
              confirmMsg += " क्या आप इस Financial Report को देखना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else if (actionId === 'delete-record') {
              confirmMsg += " क्या आप इस रिकॉर्ड को डिलीट करना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          } else {
              confirmMsg += " क्या आप इसमें जाना चाहते हैं, तो इस पर दोबारा क्लिक करें।";
          }

          speakHinglish(confirmMsg);
          clickMemory.current = { id: uid, time: now };
      }
  };

  const [expenses, setExpenses] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [monthlyProduction, setMonthlyProduction] = useState(0);
  const [workers, setWorkers] = useState([]); 
  const [reports, setReports] = useState([]); 

  const [toast, setToast] = useState(null);
  const [activeModal, setActiveModal] = useState(null); 
  const [viewHistoryType, setViewHistoryType] = useState(null); 
  const [deleteConfirmInfo, setDeleteConfirmInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasReminded, setHasReminded] = useState(false);

  // Settings
  const [workerRate, setWorkerRate] = useLocalStorage("workerRatePerMeter", 2.50);
  const [yarnConsumptionRate, setYarnConsumptionRate] = useState(0.12); 
  const [openingGreyStock, setOpeningGreyStock] = useState(0);
  const [openingYarnStock, setOpeningYarnStock] = useState(0); 

  // --- REPORT STATES ---
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportConfig, setReportConfig] = useState({
      type: '', 
      startDate: getFirstDayOfMonth(),
      endDate: getToday()
  });

  // Forms
  const [expenseForm, setExpenseForm] = useState({ date: getToday(), category: "", customCategory: "", amount: "", party: "", status: "Paid" });
  const [revenueForm, setRevenueForm] = useState({ date: getToday(), fabric: "", material: "", rate: "", qty: "", party: "", status: "Paid" });
  const [stockForm, setStockForm] = useState({ date: getToday(), material: "", type: "", qty: "", price: "", unit: "kg" });
  
  const [expenseType, setExpenseType] = useState('general');

  useEffect(() => {
    fetchAllData();
  }, []);

const fetchAllData = async () => {
    setLoading(true);
    try {
      // 🌟 NAYA LOGIC: Safe Fetch Function jo error aane par crash nahi hoga
      const safeFetchJSON = async (url) => {
          try {
              const res = await fetch(url);
              if (!res.ok) return null;
              const text = await res.text(); // Pehle text me read karein
              try {
                  return JSON.parse(text); // Phir JSON me convert karein
              } catch (e) {
                  console.error("JSON Error in URL:", url, "Response:", text);
                  return null;
              }
          } catch (e) {
              console.error("Network Error in URL:", url);
              return null;
          }
      };

      // 🌟 Sabhi APIs ko independently call karein
      const expData = await safeFetchJSON(`${API_BASE}/get_expenses.php`);
      const revData = await safeFetchJSON(`${API_BASE}/get_revenue.php`);
      const profData = await safeFetchJSON(`${API_BASE}/get_owner_profile.php`);
      const prodData = await safeFetchJSON(`${API_BASE}/get_monthly_production.php`);
      const workData = await safeFetchJSON(`${API_BASE}/get_workers.php`);
      const reportData = await safeFetchJSON(`${API_BASE}/get_reports.php`);

      // Safely Extract Expenses Data
      if (Array.isArray(expData)) {
          setExpenses(expData);
      } else if (expData && expData.status === 'success' && Array.isArray(expData.data)) {
          setExpenses(expData.data);
      } else {
          setExpenses([]); 
      }

      // Safely Extract Revenue Data
      if (Array.isArray(revData)) {
          setRevenue(revData);
      } else if (revData && revData.status === 'success' && Array.isArray(revData.data)) {
          setRevenue(revData.data);
      } else {
          setRevenue([]); 
      }

      // Baki data ki safe setting
      if (workData && workData.status === 'success') setWorkers(workData.data || []);
      if (reportData && reportData.status === 'success' && Array.isArray(reportData.data)) setReports(reportData.data);
      
      if (profData && profData.status === 'success') {
          setOwnerProfile(profData.data);
          if(profData.data.rate_per_meter) setWorkerRate(parseFloat(profData.data.rate_per_meter));
          if(profData.data.opening_stock != null) setOpeningGreyStock(parseFloat(profData.data.opening_stock));
          if(profData.data.yarn_consumption != null) setYarnConsumptionRate(parseFloat(profData.data.yarn_consumption));
          if(profData.data.opening_yarn_stock != null) setOpeningYarnStock(parseFloat(profData.data.opening_yarn_stock));
      }
      
      if (prodData && prodData.status === 'success') {
          setMonthlyProduction(parseFloat(prodData.total_meters || 0));
      }

    } catch (error) {
      console.error("Critical Fetch Error:", error);
      setToast({ message: "Network Error", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const openModalVoice = (modalName) => {
      setActiveModal(modalName);
      if (voiceAssist && !activeGuideFlow) {
          if (modalName === 'expense') {
              speakHinglish(" आपने 'Add Expense' Button ओपन किया है, अभी आप जनरल खर्चे के Button में हैं, इसमें आप टेक्सटाइल के सभी आम खर्चे लिख सकते हैं, जैसे बिल या पगार।");
          } else if (modalName === 'revenue') {
              speakHinglish(" आपने 'Add Revenue' Button ओपन किया है, इसमें आप बेचा हुआ कपड़ा और आमदनी लिख सकते हैं।");
          } else if (modalName === 'settings') {
              speakHinglish(" आपने settings Button ओपन किया है, यहाँ आप अपना डेटाबेस का Stock और कंजम्पशन मैनेज कर सकते हैं।");
          } else if (modalName === 'reports') {
              speakHinglish(" आपने Financial Report History Button ओपन किया है।");
          }
      }
  };

  const openViewHistoryVoice = (type) => {
      setViewHistoryType(type);
      if(voiceAssist && !activeGuideFlow) {
          speakHinglish(`आपने ${type === 'expense' ? 'खर्चा' : 'आमदनी'} History Button ओपन किया है।`);
      }
  };

 const openReportModalVoice = () => {
      // 👇 यहाँ type को 'all' कर दिया है
      setReportConfig({ type: 'all', startDate: getFirstDayOfMonth(), endDate: getToday() });
      setShowReportModal(true);
      if(voiceAssist && !activeGuideFlow) speakHinglish(" आपने Download Performance Report Button ओपन किया है।");
  };

 const closeModalsVoice = (e) => {
      if(e) { e.preventDefault(); e.stopPropagation(); }
      
      // 👇 जादू: कोई भी मोडल क्लोज होते ही सारी आवाज़ें तुरंत बंद हो जाएंगी
      if (window.speechSynthesis) {
          window.speechSynthesis.cancel(); 
      }

      setPendingConfirm(null); 
      clickMemory.current = { id: null, time: 0 }; 
      
      if (activeGuideFlow) { endSmartGuide(true); }

      setActiveModal(null); 
      setViewHistoryType(null); 
      setShowReportModal(false);
      setShowGuideModal(false);
      
      setExpenseType('general'); 
      setExpenseForm({ date: getToday(), category: "", customCategory: "", amount: "", party: "", status: "Paid" });
      setRevenueForm({ date: getToday(), fabric: "", material: "", rate: "", qty: "", party: "", status: "Paid" });
  };

  // --- CALCULATIONS ---
  const financials = useMemo(() => {
    const totalRev = revenue.reduce((sum, r) => sum + ((parseFloat(r.ratePerMeter)||0) * (parseFloat(r.metersSold)||0)), 0);
    const totalExp = expenses.reduce((sum, e) => sum + (parseFloat(e.amount)||0), 0);
    const profit = totalRev - totalExp;
    const calculatedSalary = monthlyProduction * workerRate;
    return { revenue: totalRev, expense: totalExp, profit, calculatedSalary };
  }, [revenue, expenses, monthlyProduction, workerRate]);

const groupedExpenses = useMemo(() => {
    const groups = {};
    expenses.forEach(e => {
        const cat = e.category;
        if (!groups[cat]) groups[cat] = 0;
        groups[cat] += parseFloat(e.amount);
    });
    return groups;
  }, [expenses]);

  // --- NEW: REVENUE BREAKDOWN LOGIC ---
  const groupedRevenue = useMemo(() => {
    const groups = {};
    revenue.forEach(r => {
        const cat = r.fabricType ? `${r.fabricType} Fabric` : "Other Sales";
        if (!groups[cat]) groups[cat] = 0;
        groups[cat] += ((parseFloat(r.ratePerMeter)||0) * (parseFloat(r.metersSold)||0));
    });
    return groups;
  }, [revenue]);

  const stocks = useMemo(() => {
    const totalSoldMeters = revenue.reduce((sum, r) => sum + parseFloat(r.metersSold||0), 0);
    const greyStock = parseFloat(openingGreyStock) + parseFloat(monthlyProduction) - totalSoldMeters;

    const yarnPurchasedKg = expenses
        .filter(e => e.category.toLowerCase().includes('yarn'))
        .reduce((sum, e) => {
            return sum + (parseFloat(e.amount) / 250); 
        }, 0);
    
    const yarnConsumed = monthlyProduction * yarnConsumptionRate;
    const currentYarnStock = Math.max(0, parseFloat(openingYarnStock) + yarnPurchasedKg - yarnConsumed); 

    return { 
        grey: greyStock.toFixed(1), 
        yarn: currentYarnStock.toFixed(1),
        yarnAlert: currentYarnStock < 50 
    };
  }, [revenue, expenses, monthlyProduction, openingGreyStock, yarnConsumptionRate, openingYarnStock]);

  const ledger = useMemo(() => {
    const receivables = revenue.filter(r => r.status === 'Pending').map(r => ({
        party: r.party || 'Unknown', amount: parseFloat(r.ratePerMeter)*parseFloat(r.metersSold), date: r.date, type: 'Fabric Sale', status: r.status
    }));
    const payables = expenses.filter(e => e.status === 'Pending').map(e => ({
        party: e.party || 'Unknown', amount: parseFloat(e.amount), date: e.date, type: e.category, status: e.status
    }));
    return { receivables, payables };
  }, [revenue, expenses]);

  // --- SMART VOICE REMINDER FOR LEDGER (UDHAARI) ---
  useEffect(() => {
      if (!loading && (expenses.length > 0 || revenue.length > 0) && !hasReminded) {
          const totalReceivables = ledger.receivables.reduce((sum, item) => sum + item.amount, 0);
          const totalPayables = ledger.payables.reduce((sum, item) => sum + item.amount, 0);

          if (totalReceivables > 0 || totalPayables > 0) {
              let msg = " Inventory डेटा Load हो गया है, ध्यान दें, ";
              if (totalReceivables > 0) { msg += ` आपको लोगों से Total ${formatVoiceCurrency(totalReceivables)} लेने हैं। `; }
              if (totalPayables > 0) { msg += ` और आपको मार्केट में Total ${formatVoiceCurrency(totalPayables)} देने हैं। `; }
              msg += " पूरी जानकारी के लिए पार्टी लेजर Button चेक करें।";
              if (voiceAssist) { speakHinglish(msg); }
              setHasReminded(true); 
          }
      }
  }, [loading, expenses, revenue, ledger, hasReminded, voiceAssist, speakHinglish]);

  const expenseCategories = useMemo(() => [
    { label: "Electricity Bill", value: "Electricity Bill" },
    { label: "Rent", value: "Rent" },
    { label: "Maintenance", value: "Maintenance" },
    { label: "Workers Salary (Auto)", value: "Salaries", autoValue: financials.calculatedSalary },
    { label: "Other", value: "Other" }
  ], [financials.calculatedSalary]);

  // --- API HANDLERS ---
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount || !expenseForm.category) {
        if(voiceAssist) speakHinglish(" ध्यान दें, आपने कुछ ज़रूरी जानकारी खाली छोड़ दी है। कृपया फॉर्म पूरा भरें।");
        return setToast({message:"Fill all fields", type:"error"});
    }
    
    const finalCat = expenseForm.category === "Other" ? expenseForm.customCategory : expenseForm.category;
    const payload = {
        date: expenseForm.date, category: finalCat, amount: parseFloat(expenseForm.amount),
        party: expenseForm.party, status: expenseForm.status 
    };

    try {
        const res = await fetch(`${API_BASE}/save_expense.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const data = await res.json();
        if(data.status === 'success') { 
            if(voiceAssist && !activeGuideFlow) speakHinglish(" आपका Expense यानी खर्चा Successfully Save हो गया है।");
            setToast({message: "Expense Added", type:"success"}); 
            fetchAllData(); 
            closeModalsVoice(); 
        } 
        else { setToast({message: "Error: " + data.message, type:"error"}); }
    } catch (err) { setToast({message: "Server Connection Failed", type:"error"}); }
  };

  const handleRevenueSubmit = async (e) => {
    e.preventDefault();
    if (!revenueForm.rate || !revenueForm.qty) {
        if(voiceAssist) speakHinglish("ध्यान दें, आपने रेट और मीटर की जानकारी खाली छोड़ दी है।");
        return setToast({message:"Fill all fields", type:"error"});
    }
    
    const payload = {
        date: revenueForm.date, fabricType: revenueForm.fabric, material: revenueForm.material,
        ratePerMeter: parseFloat(revenueForm.rate), metersSold: parseFloat(revenueForm.qty),
        party: revenueForm.party, status: revenueForm.status
    };
    try {
        const res = await fetch(`${API_BASE}/save_revenue.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const data = await res.json();
        if(data.status === 'success') { 
            if(voiceAssist && !activeGuideFlow) speakHinglish(" आपके Revenue यानी आमदनी का Record Save हो गया है।");
            setToast({message: "Revenue Added", type:"success"}); 
            fetchAllData(); 
            closeModalsVoice(); 
        } 
        else { setToast({message: "Error: " + data.message, type:"error"}); }
    } catch (err) { setToast({message: "Server Connection Failed", type:"error"}); }
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    if (!stockForm.qty || !stockForm.price) {
        if(voiceAssist) speakHinglish("ध्यान दें, आपने किलो और रेट की जानकारी खाली छोड़ दी है।");
        return setToast({message:"Fill all fields", type:"error"});
    }
    const expensePayload = {
        date: stockForm.date, category: `Purchased Yarn (${stockForm.material})`,
        amount: parseFloat(stockForm.qty) * parseFloat(stockForm.price),
        party: "Supplier", status: "Paid"
    };
    try {
        await fetch(`${API_BASE}/save_expense.php`, { method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(expensePayload) });
        if(voiceAssist && !activeGuideFlow) speakHinglish(" नए Yarn(धागे) का Stock Save हो गया है।");
        setToast({message: "Yarn Stock Recorded", type:"success"}); 
        fetchAllData(); 
        closeModalsVoice();
    } catch (err) { setToast({message: "Error", type:"error"}); }
  };

  const handleSettingsSubmit = async (e) => {
      e.preventDefault();
      try {
          const payload = {
              opening_stock: parseFloat(openingGreyStock),
              yarn_consumption: parseFloat(yarnConsumptionRate),
              opening_yarn_stock: parseFloat(openingYarnStock) 
          };
          const res = await fetch(`${API_BASE}/update_settings.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });
          const data = await res.json();
          if(data.status === 'success') {
              setToast({message: "Settings Saved to Database", type:"success"});
              closeModalsVoice();
          } else {
              setToast({message: "Error: " + data.message, type:"error"});
          }
      } catch(err) {
          setToast({message: "Network Error saving settings", type:"error"});
      }
  };

  const deleteEntry = async (id, type) => {
    const endpoint = type === 'expense' ? 'delete_expense.php' : 'delete_revenue.php';
    try {
        const res = await fetch(`${API_BASE}/${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: id }) });
        const data = await res.json();
        if(data.status === 'success') { 
            if(voiceAssist) speakHinglish(" Record हमेशा के लिए Delete हो गया है।");
            setToast({message: "Deleted", type:"success"}); 
            fetchAllData(); 
        } 
        else { setToast({message: "Delete Failed", type:"error"}); }
    } catch (err) { setToast({message: "Delete Error", type:"error"}); }
  };

  // --- PDF GENERATION (Premium WorkerHistory Style) ---
  const generatePDF = async (customConfig = null, mode = 'download') => {
    const config = customConfig || reportConfig;
    const filteredExp = expenses.filter(e => e.date >= config.startDate && e.date <= config.endDate);
    const filteredRev = revenue.filter(r => r.date >= config.startDate && r.date <= config.endDate);
    
    const todayStr = new Date().toLocaleDateString("en-GB");
    const range = `${formatDate(config.startDate)} TO ${formatDate(config.endDate)}`;

    if (!customConfig && mode === 'download') {
        try {
            const reportPayload = { report_date: getToday(), date_range: range, config_json: JSON.stringify(config) };
            await fetch(`${API_BASE}/save_report.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(reportPayload) });
            fetchAllData(); 
            setToast({ message: "Report Saved to History", type: "success" });
        } catch (error) { console.error("Failed to save report to DB", error); }
    }

    // --- TEXTILE INFO SETUP ---
    const TEXTILE_INFO = {
        name: ownerProfile?.textile_name ? ownerProfile.textile_name.toUpperCase() : "TEXTILE FACTORY",
        owner: ownerProfile?.owner_name ? ownerProfile.owner_name.toUpperCase() : "OWNER NAME",
        mobile: ownerProfile?.mobile || "-",
        email: ownerProfile?.email || "-",
        gst: ownerProfile?.gst_number || "-",
        address: ownerProfile?.address || "-",
        totalLooms: ownerProfile?.total_looms || "-"
    };

    // --- PDF INITIALIZATION (Landscape A4) ---
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const centerX = 297 / 2; // Landscape width is 297mm

    // --- WATERMARK FUNCTION ---
    const drawWatermark = () => {
        doc.setTextColor(240, 245, 250); 
        doc.setFontSize(45);
        doc.setFont("helvetica", "bold");
        doc.text(TEXTILE_INFO.name, centerX, 110, { align: "center", angle: 35 });
    };

    drawWatermark();
    
    // Override addPage to include watermark on every new page
    const originalAddPage = doc.addPage.bind(doc);
    doc.addPage = function(...args) {
        originalAddPage(...args);
        drawWatermark(); 
        return this;     
    };

    // --- HEADER DESIGN ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42); 
    doc.text("INVENTORY & FINANCIAL REPORT", centerX, 15, { align: "center" });

    const downloadTime = new Date().toLocaleString('en-IN', { 
        day: 'numeric', month: 'short', year: 'numeric', 
        hour: '2-digit', minute: '2-digit', hour12: true 
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Downloaded: ${downloadTime}`, 297 - 14, 15, { align: "right" });

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 65, 85);
    doc.text(TEXTILE_INFO.name, centerX, 24, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);

    const line1 = `Owner: ${TEXTILE_INFO.owner}   |   Total Looms: ${TEXTILE_INFO.totalLooms}   |   Mobile: ${TEXTILE_INFO.mobile}`;
    doc.text(line1, centerX, 30, { align: "center" });

    const line2 = `Email: ${TEXTILE_INFO.email}   |   GST No: ${TEXTILE_INFO.gst}`;
    doc.text(line2, centerX, 35, { align: "center" });

    const line3 = `Address: ${TEXTILE_INFO.address}`;
    doc.text(line3, centerX, 40, { align: "center" });

    doc.setDrawColor(226, 232, 240); 
    doc.setLineWidth(0.5);
    doc.line(14, 44, 297 - 14, 44);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(`Period: ${range}`, centerX, 52, { align: "center" });

    let currentY = 58;

    // --- REVENUE REPORT TABLE ---
    if (filteredRev.length > 0) {
        doc.setFontSize(11);
        doc.text("REVENUE REPORT", 14, currentY);
        currentY += 4;
        
        autoTable(doc, {
          startY: currentY, 
          head: [['Date', 'Fabric Type', 'Material', 'Qty & Rate', 'Party Name', 'Total Amount', 'Status']],
          body: filteredRev.map(r => [
              formatDate(r.date), 
              r.fabricType || '-',
              r.material || '-',
              `${r.metersSold} Meter @ ${formatCurrency(r.ratePerMeter)}/Meter`, 
              r.party || '-', 
              formatCurrency(r.ratePerMeter * r.metersSold), 
              r.status || 'Paid'
          ]),
          theme: 'grid',
          headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9, halign: 'center' }, 
          alternateRowStyles: { fillColor: [247, 247, 247] },
          styles: { fillColor: [255, 255, 255], fontSize: 8.5, halign: 'center', cellPadding: 4, textColor: [15, 23, 42] },
          columnStyles: { 
              0: { fontStyle: 'bold', halign: 'left' }, 
              5: { fontStyle: 'bold', textColor: [22, 163, 74] } 
          },
          margin: { left: 14, right: 14 }
        });
        currentY = doc.lastAutoTable.finalY + 15;
    }

    // --- EXPENSE REPORT TABLE ---
    if (currentY > 150) { doc.addPage(); currentY = 25; }
    
    if (filteredExp.length > 0) {
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text("EXPENSE REPORT", 14, currentY);
        currentY += 4;

        autoTable(doc, {
          startY: currentY, 
          head: [['Date', 'Expense Category', 'Party Name', 'Amount', 'Status']],
          body: filteredExp.map(e => [
              formatDate(e.date), 
              e.category, 
              e.party || '-', 
              formatCurrency(e.amount), 
              e.status || 'Paid'
          ]),
          theme: 'grid',
          headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9, halign: 'center' }, 
          alternateRowStyles: { fillColor: [247, 247, 247] },
          styles: { fillColor: [255, 255, 255], fontSize: 8.5, halign: 'center', cellPadding: 4, textColor: [15, 23, 42] },
          columnStyles: { 0: { fontStyle: 'bold', halign: 'left' }, 3: { fontStyle: 'bold', textColor: [220, 38, 38] } },
          margin: { left: 14, right: 14 }
        });
        currentY = doc.lastAutoTable.finalY + 15;
    }

   // --- FINANCIAL SUMMARY TABLE (PROPER BOXED STYLE) ---
    if (currentY > 155) { doc.addPage(); currentY = 25; }

    const totRev = filteredRev.reduce((sum, r) => sum + (r.ratePerMeter * r.metersSold), 0);
    const pendingRev = filteredRev.filter(r => r.status === 'Pending').reduce((sum, r) => sum + (r.ratePerMeter * r.metersSold), 0);
    
    const totExp = filteredExp.reduce((sum, e) => sum + parseFloat(e.amount), 0);
    const pendingExp = filteredExp.filter(e => e.status === 'Pending').reduce((sum, e) => sum + parseFloat(e.amount), 0);
    
    const netP = totRev - totExp;

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("FINANCIAL SUMMARY", 297 - 14 - 100, currentY); // Right aligned header
    currentY += 4;

    const revText = pendingRev > 0 ? `Total Revenue\n(Pending to Receive: ${formatCurrency(pendingRev)})` : 'Total Revenue';
    const expText = pendingExp > 0 ? `Total Expenses\n(Pending to Pay: ${formatCurrency(pendingExp)})` : 'Total Expenses';

    autoTable(doc, {
        startY: currentY,
        head: [['Particulars', 'Total Amount']],
        body: [
            [revText, { content: formatCurrency(totRev), styles: { textColor: [22, 163, 74], valign: 'middle' } }],
            [expText, { content: formatCurrency(totExp), styles: { textColor: [220, 38, 38], valign: 'middle' } }],
            [{ content: netP >= 0 ? 'NET PROFIT' : 'NET LOSS', styles: { fillColor: [226, 232, 240], fontStyle: 'bold', fontSize: 11, valign: 'middle' } },
             { content: formatCurrency(netP), styles: { fillColor: [226, 232, 240], fontStyle: 'bold', fontSize: 11, textColor: netP >= 0 ? [22, 163, 74] : [220, 38, 38], valign: 'middle' } }]
        ],
        theme: 'grid',
        headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9, halign: 'center' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: { fontSize: 9.5, halign: 'right', cellPadding: 6, textColor: [15, 23, 42], fontStyle: 'bold' },
        columnStyles: { 
            0: { halign: 'left', cellWidth: 55 }, 
            1: { halign: 'right', cellWidth: 45 } 
        },
        margin: { left: 297 - 14 - 100, right: 14 }, 
    });

    // --- PAGE NUMBERS ---
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(`Page ${i} of ${totalPages}`, 297 - 14, 210 - 10, { align: "right" }); 
    }

    // --- OUTPUT ---
    if (mode === 'view') { window.open(doc.output('bloburl'), '_blank'); } 
    else { 
        if(voiceAssist && !activeGuideFlow) speakHinglish("आपकी Financial Report फाइल Successfully Download हो गई है।");
        doc.save(`Inventory_Report_${todayStr.replace(/\//g, '-')}.pdf`); 
        setShowReportModal(false); 
    }
  };

  // 🌟 NAYA LOGIC: Check all Inventory Modals
    const isAnyModalOpen = showGuideModal || activeModal || showReportModal || viewHistoryType || deleteConfirmInfo;

    // ===== 🌟 PREMIUM SCROLL LOCK (Hostinger / Mobile Fix) 🌟 =====
    useEffect(() => {
        const container = document.getElementById('inventory-view-container');
        if (isAnyModalOpen) {
            document.body.style.overflow = 'hidden'; 
            if (container) container.style.overflowY = 'hidden'; // Popup खुलने पर मेन कंटेनर को भी लॉक करेगा
        } else {
            document.body.style.overflow = 'auto';   
            if (container) container.style.overflowY = 'auto';   // Popup बंद होने पर स्क्रॉल वापस चालू करेगा
        }
        return () => { 
            document.body.style.overflow = 'auto'; 
            const cont = document.getElementById('inventory-view-container');
            if (cont) cont.style.overflowY = 'auto';
        };
    }, [isAnyModalOpen]);

  return (
  <div id="inventory-view-container" style={{...styles.container, background: appBg}}>
      <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap'); 
          * { font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; } 
          @keyframes zoomIn { 0% {transform: scale(0.9); opacity: 0;} 100% {transform: scale(1); opacity: 1;} }
          @keyframes pulseGuide { 0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); } 70% { transform: scale(1); box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); } 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
          
          /* Spotlight Magic CSS */
          .smart-guide-target {
              box-shadow: 0 0 0 4px #10B981, 0 0 20px #10B981, 0 0 0 9999px rgba(0, 0, 0, 0.75) !important;
              outline: 3px solid white !important;
              background-color: #fff !important; 
              color: #000 !important;
              transition: all 0.3s ease !important;
              position: relative !important;
              z-index: 100005 !important;
              pointer-events: auto !important;
          }

        /* 👇 ये CSS आपकी सेटिंग के कलर को लॉक (Force) कर देगी 👇 */
          .force-green { color: #16A34A !important; }
          .force-green * { color: #16A34A !important; }
          
          .force-red { color: #DC2626 !important; }
          .force-red * { color: #DC2626 !important; }
          
         /* 🚀 PERFORMANCE & SMOOTHNESS CSS 🚀 */
          * { -webkit-tap-highlight-color: transparent; scroll-behavior: smooth; font-size: 14px !important; } /* 🌟 14px Text Size Lock kiya */
          ::-webkit-scrollbar { width: 4px; height: 0px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.2); border-radius: 10px; }
          body { 
              -webkit-font-smoothing: antialiased; 
              -moz-osx-font-smoothing: grayscale; 
              overscroll-behavior-y: none; 
              zoom: 0.95 !important; /* 🌟 95% Layout Zoom Lock kiya */
          }
      `}</style>
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

     {/* ===== 🌟 PREMIUM HEADER (Inventory.js) 🌟 ===== */}
      <div style={{...styles.header, background: '#ffffff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', zIndex: activeGuideFlow ? 20 : 1000, pointerEvents: activeGuideFlow ? 'none' : 'auto'}}>
        <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
          {/* Back Button with Premium Look */}
          <button onClick={(e) => handleVoiceClick(e, " यह बैक Button है, यहाँ से आप पिछले पेज पर वापस जा सकते हैं।", onBack)} style={{...styles.backBtn, background: '#f8fafc', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'}}> 
            <ArrowLeft size={18} color="#475569"/> 
          </button>
          <h1 style={{fontSize:'16px', fontWeight:'600', color:'#0F172A', margin:0, letterSpacing:'-0.5px'}}>
            Inventory
          </h1>
        </div>
        
        <div style={{display:'flex', gap:'8px'}}>
            {/* Smart Guide Button with Gradient Icon Style */}
            <button 
                onClick={() => {
                    speakHinglish(" Smart Live Guide Menu ओपन हो गया है, आप किस Topic के बारे में सीखना चाहते हैं? नीचे दिए गए options में से चुनें, 1. Yarn Purchase यानी धागा खरीदी की Entry कैसे Add करें?, 2. Revenue यानी आमदनी की Entry कैसे Add करें?, 3. Financial Report कि PDF File कैसे Download करें?, 4. पार्टी लेजर में किसको कितना पैसा देना Baki है (Amount To Pay) और किससे कितना पैसा लेना Baki है (Amount To Receive) कैसे चेक करें?");
                    setShowGuideModal(true);
                }} 
                style={{
                    padding: '6px 8px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', border: '1px solid #BAE6FD',
                    boxShadow: '0 2px 6px rgba(2, 132, 199, 0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s'
                }}
            >
                <HelpCircle size={16} color="#0284C7"/>
            </button>

            {/* Settings Button with Premium Look */}
            <button 
                onClick={(e) => handleVoiceClick(e, " यह Settings है, यहाँ से आप अपना धागा और कपड़ा Stock मैनेज कर सकते हैं।", () => openModalVoice('settings'), 'btn-settings')} 
                style={{...styles.iconBtnSquare, background: '#f8fafc', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'}}
            >
                <Settings size={16} color="#475569"/>
            </button>
        </div>
      </div>

      <div style={{...styles.content, pointerEvents: activeGuideFlow ? 'none' : 'auto'}}>
        {loading && <div style={{textAlign:'center', padding:'20px', color:'#111', fontWeight:'600', fontSize:'13px'}}>Loading Data...</div>}

        {stocks.yarnAlert && (
             <div style={{
                 background:'rgba(254, 242, 242, 0.8)', border:'1px solid rgba(220, 38, 38, 0.3)', padding:'12px 16px', borderRadius:'16px', 
                 marginBottom:'20px', display:'flex', alignItems:'center', gap:'12px', backdropFilter: 'blur(10px)',
                 color:'#991B1B', fontSize:'11px', fontWeight:'400', boxShadow: '0 4px 10px rgba(220, 38, 38, 0.1)'
             }}>
                 <div style={{background:'#FCA5A5', padding:'6px', borderRadius:'50%'}}><AlertCircle size={14} color="white"/></div>
                 <span>Order Yarn Now! Stock Low (<b>{stocks.yarn} kg</b>)</span>
             </div>
        )}

       {/* ==========================================
            🌟 PREMIUM UNIFIED BUSINESS DASHBOARD 🌟
        ========================================== */}
        <div style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%)',
            backdropFilter: 'blur(24px)',
            borderRadius: '24px',
            padding: '16px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.04), inset 0 2px 0 rgba(255,255,255,1)',
            border: '1px solid rgba(255,255,255,0.7)',
            marginBottom: '20px'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '10px', fontWeight: '600', color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>Business Summary</h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                
                {/* ROW 1: INVENTORY (Production & Yarn) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {/* Production Stock */}
                    <div onClick={(e) => handleVoiceClick(e, ` यह Production Stock का बॉक्स है, आपके पास अभी ${formatVoiceNumber(stocks.grey)} मीटर तैयार कपड़ा मौजूद है।`, null)}
                         style={{ background: '#ffffff', padding: '12px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '6px', cursor: 'pointer', transition: 'all 0.2s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.15)' }}><Layers size={14} color="#0284C7"/></div>
                            <span style={{ fontSize: '10px', fontWeight: '400', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Production Stock</span>
                        </div>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#0F172A', letterSpacing: '-0.3px' }}>{stocks.grey} <span style={{ fontSize: '9px', color: '#0284C7', fontWeight: '400' }}>Meter</span></span>
                    </div>

                    {/* Yarn Stock */}
                    <div onClick={(e) => handleVoiceClick(e, ` यह Yarn Stock का बॉक्स है, आपके पास अभी ${formatVoiceNumber(stocks.yarn)} किलो धागा बचा है, ${stocks.yarnAlert ? 'ध्यान दें, धागा कम है, नया आर्डर करें।' : ''}`, null)}
                         style={{ background: '#ffffff', padding: '12px', borderRadius: '16px', border: stocks.yarnAlert ? '1px solid rgba(220, 38, 38, 0.4)' : '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '6px', cursor: 'pointer', transition: 'all 0.2s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ background: stocks.yarnAlert ? 'linear-gradient(135deg, #FEE2E2, #FCA5A5)' : 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: stocks.yarnAlert ? '0 2px 6px rgba(220, 38, 38, 0.15)' : '0 2px 6px rgba(79, 70, 229, 0.15)' }}>
                                <Package size={14} color={stocks.yarnAlert ? "#DC2626" : "#4F46E5"}/>
                            </div>
                            <span style={{ fontSize: '10px', fontWeight: '400', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Yarn Stock</span>
                        </div>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: stocks.yarnAlert ? '#DC2626' : '#0F172A', letterSpacing: '-0.3px' }}>{stocks.yarn} <span style={{ fontSize: '9px', color: stocks.yarnAlert ? '#DC2626' : '#64748B', fontWeight: '400' }}>Kg</span></span>
                        
                        {stocks.yarnAlert && (
                            <div style={{ marginTop:'2px', color:'#DC2626', fontSize:'8px', fontWeight:'400', background:'rgba(254, 242, 242, 0.8)', padding:'2px 6px', borderRadius:'6px', width:'fit-content', display:'flex', alignItems:'center', gap:'4px', border: '1px solid rgba(220, 38, 38, 0.2)' }}>
                                <AlertCircle size={8}/> Low Stock
                            </div>
                        )}
                    </div>
                </div>

                {/* ROW 2: FINANCE (Expenses & Revenue) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {/* Expenses */}
                    <div onClick={(e) => handleVoiceClick(e, ` यह Total Expenses यानी total खर्चे का बॉक्स है, आपका total खर्चा ${formatVoiceCurrency(financials.expense)} है।`, () => openViewHistoryVoice('expense'), 'view-exp-history')}
                         style={{ background: '#ffffff', padding: '12px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '6px', cursor: 'pointer', position: 'relative', transition: 'all 0.2s' }}>
                        <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#f8fafc', padding: '4px', borderRadius: '6px' }}><Eye size={12} color="#64748B"/></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ background: 'linear-gradient(135deg, #FEE2E2, #FCA5A5)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(220, 38, 38, 0.15)' }}><TrendingDown size={14} color="#DC2626"/></div>
                            <span style={{ fontSize: '10px', fontWeight: '400', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Total Experience</span>
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#DC2626', letterSpacing: '-0.3px' }}>{formatCurrency(financials.expense)}</span>
                    </div>

                    {/* Revenue */}
                    <div onClick={(e) => handleVoiceClick(e, ` यह Total revenue यानी total आमदनी का बॉक्स है, आपकी total आमदनी ${formatVoiceCurrency(financials.revenue)} है।`, () => openViewHistoryVoice('revenue'), 'view-rev-history')}
                         style={{ background: '#ffffff', padding: '12px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '6px', cursor: 'pointer', position: 'relative', transition: 'all 0.2s' }}>
                        <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#f8fafc', padding: '4px', borderRadius: '6px' }}><Eye size={12} color="#64748B"/></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ background: 'linear-gradient(135deg, #D1FAE5, #6EE7B7)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(5, 150, 105, 0.15)' }}><TrendingUp size={14} color="#059669"/></div>
                            <span style={{ fontSize: '10px', fontWeight: '400', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Total Revenue</span>
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#16A34A', letterSpacing: '-0.3px' }}>{formatCurrency(financials.revenue)}</span>
                    </div>
                </div>

                {/* ROW 3: NET PROFIT/LOSS (Full Width) */}
                <div onClick={(e) => handleVoiceClick(e, ` ये नेट Profit और Loss का बॉक्स है, total Expense निकालने के बाद आपका ${financials.profit >= 0 ? ' मुनाफा' : ' नुकसान'} ${formatVoiceCurrency(Math.abs(financials.profit))} है।`, null)}
                     style={{ background: financials.profit >= 0 ? '#F0FDF4' : '#FEF2F2', padding: '14px 16px', borderRadius: '16px', border: financials.profit >= 0 ? '1px solid #A7F3D0' : '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ background: financials.profit >= 0 ? 'linear-gradient(135deg, #D1FAE5, #6EE7B7)' : 'linear-gradient(135deg, #FEE2E2, #FCA5A5)', padding: '8px', borderRadius: '10px', boxShadow: financials.profit >= 0 ? '0 2px 6px rgba(5, 150, 105, 0.15)' : '0 2px 6px rgba(220, 38, 38, 0.15)' }}>
                            <Wallet size={16} color={financials.profit >= 0 ? "#059669" : "#DC2626"}/>
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: '600', color: financials.profit >= 0 ? '#166534' : '#991B1B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {financials.profit >= 0 ? "NET PROFIT" : "NET LOSS"}
                        </span>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: financials.profit >= 0 ? '#16A34A' : '#DC2626', letterSpacing: '-0.5px' }}>
                        {formatCurrency(financials.profit)}
                    </span>
                </div>
                
            </div>
        </div>

        {/* 🌟 PREMIUM CUSTOM TABS FOR INVENTORY (Pill Switch Design) 🌟 */}
        <div style={{ 
            display: 'flex', 
            background: '#F1F5F9', /* 🌟 अनसिलेक्टेड बॉक्स का डार्क/ग्रे बैकग्राउंड */
            padding: '4px', 
            borderRadius: '16px', /* पिल (Pill) शेप */
            marginBottom: '16px',
            boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.05)', /* अंदर की तरफ 3D शैडो */
            border: '1px solid rgba(226, 232, 240, 0.8)'
        }}>
            <button 
                onClick={(e) => handleVoiceClick(e, " यह ओवरव्यू Button है, यहाँ आप Profit और Loss का Total हिसाब देख सकते हैं, ", () => setActiveTab('overview'), 'tab-overview', activeTab === 'overview', " यहाँ आप Profit और Loss का Total हिसाब देख सकते हैं।")} 
                style={activeTab === 'overview' 
                    ? { flex: 1, padding: '8px 12px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', fontWeight: '600', color: '#0F172A', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' } 
                    : { flex: 1, padding: '8px 12px', background: 'transparent', border: '1px solid transparent', borderRadius: '12px', fontWeight: '400', color: '#64748B', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
            >
                <div style={{ background: activeTab === 'overview' ? 'linear-gradient(135deg, #E0F2FE, #7DD3FC)' : 'transparent', padding: '4px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: activeTab === 'overview' ? '0 2px 6px rgba(2, 132, 199, 0.2)' : 'none' }}>
                    <Receipt size={14} color={activeTab === 'overview' ? '#0369A1' : '#94A3B8'}/>
                </div>
                OVERVIEW
            </button>
            
            <button 
                id="tab-ledger" 
                onClick={(e) => handleVoiceClick(e, " यह पार्टी लेजर Button है, यहाँ किसको कितना पैसा देना बाकी है, और किससे कितना पैसा लेना बाकी है, वो देख सकते हैं, ", () => setActiveTab('ledger'), 'tab-ledger', activeTab === 'ledger', " यहाँ किसको कितना पैसा देना बाकी है, और किससे कितना पैसा लेना बाकी है, वो देख सकते हैं।")} 
                style={activeTab === 'ledger' 
                    ? { flex: 1, padding: '8px 12px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', fontWeight: '600', color: '#0F172A', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' } 
                    : { flex: 1, padding: '8px 12px', background: 'transparent', border: '1px solid transparent', borderRadius: '12px', fontWeight: '400', color: '#64748B', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
            >
                <div style={{ background: activeTab === 'ledger' ? 'linear-gradient(135deg, #D1FAE5, #6EE7B7)' : 'transparent', padding: '4px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: activeTab === 'ledger' ? '0 2px 6px rgba(5, 150, 105, 0.2)' : 'none' }}>
                    <Wallet size={14} color={activeTab === 'ledger' ? '#047857' : '#94A3B8'}/>
                </div>
                PARTY LEDGER
            </button>
        </div>

        {activeTab === 'overview' && (
            <>
               <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'24px'}}>
                  <button id="btn-add-expense" onClick={(e) => handleVoiceClick(e, " यह Add Expense बटन है, यहाँ से आप नया Expense यानी खर्चा जोड़ सकते हैं।", () => openModalVoice('expense'), 'btn-add-exp')} style={styles.premiumButtonStyle}>
                    <Plus size={16} strokeWidth={3} /> Add Expense
                  </button>
                  <button id="btn-add-revenue" onClick={(e) => handleVoiceClick(e, " यह Add Revenue यानी आमदनी ऐड करने का बटन है, यहाँ से आप नई revenue यानी नई आमदनी जोड़ सकते हैं।", () => openModalVoice('revenue'), 'btn-add-rev')} style={styles.premiumButtonStyle}>
                    <Plus size={16} strokeWidth={3} /> Add Revenue
                  </button>
                </div>

                <div style={styles.card}>
                  <div style={{padding:'20px'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                         <div style={{display:'flex', alignItems:'center', gap:'10px'}} onClick={(e) => handleVoiceClick(e, " ये पी एंड एल स्टेटमेंट यानी Profit and Loss Statement है।", null)}>
                             <div style={{width:'36px', height:'36px', borderRadius:'10px', background:'rgba(255,255,255,0.4)', border:'1px solid rgba(255,255,255,0.4)', display:'flex', alignItems:'center', justifyContent:'center'}}>
                                <img src={IMAGES.pnl} style={{width:'20px', height:'20px'}} alt=""/>
                             </div>
                             <h2 style={{fontSize:'15px', fontWeight:'400', color:'#111', margin:0}}>Financial Performance</h2>
                         </div>
                        
                        <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                            <button id="btn-download-pdf" onClick={(e) => handleVoiceClick(e, " यहाँ से आप अपने Expenses यानी खर्चे और  Revenue यानी आमदनी की Financial Report Download कर सकते हैं।", () => openReportModalVoice(), 'btn-pdf')} style={{background:'rgba(255,255,255,0.4)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'10px', padding:'6px 10px', cursor:'pointer', color:'#111', fontSize:'10px', fontWeight:'400', display:'flex', alignItems:'center', gap:'4px'}}>
                                <Download size={12}/> Financial Report
                            </button>
                            <button onClick={(e) => handleVoiceClick(e, " यहाँ से आप अपनी पुरानी Financial Report की History देख सकते हैं।", () => openModalVoice('reports'), 'btn-history')} style={{background:'rgba(255,255,255,0.4)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'10px', padding:'6px 10px', cursor:'pointer', color:'#111', fontSize:'10px', fontWeight:'400', display:'flex', alignItems:'center', gap:'4px'}}>
                                History
                            </button>
                        </div>
                    </div>

                   <div style={{background:'#ffffff', padding:'16px', borderRadius:'20px', border:'1px solid #f1f5f9', boxShadow: '0 4px 10px rgba(0,0,0,0.03)'}}>
  
  {/* --- REVENUE SECTION (GREEN) --- */}
  <div onClick={(e) => handleVoiceClick(e, " यहाँ नीचे रेवेन्यू यानी आमदनी की डिटेल्स हैं।", null)} style={{fontSize:'10px', fontWeight:'400', color:'#64748B', marginBottom:'8px', letterSpacing:'0.5px'}}>
    REVENUE BREAKDOWN
  </div>
  
  {Object.keys(groupedRevenue).length === 0 && <PnLRow label="No revenue recorded" amount="-" isSub />}
  
  {Object.entries(groupedRevenue).map(([category, amount]) => (
     <PnLRow key={category} label={category} amount={formatCurrency(amount)} isSub voiceText={`${category} बेचकर आपने लगभग ${formatVoiceCurrency(amount)} कमाए हैं।`} handleVoiceClick={handleVoiceClick} />
  ))}
  
  <div style={{borderBottom:'1px solid #e2e8f0', marginBottom:'16px', paddingBottom:'4px'}}>
      <PnLRow 
          label="Total Revenue" 
          amount={formatCurrency(financials.revenue)} 
          isTotal 
          color="#16A34A" /* GREEN COLOR FOR REVENUE */
          icon={<TrendingUp size={16} color="#16A34A" strokeWidth={2.5} />} /* REVENUE ICON */
          voiceText={` आपका Total revenue यानी Total आमदनी ${formatVoiceCurrency(financials.revenue)} है।`} 
          handleVoiceClick={handleVoiceClick}
      />
  </div>

  {/* --- EXPENSE SECTION (RED) --- */}
  <div onClick={(e) => handleVoiceClick(e, " यहाँ नीचे सारे अलग-अलग केटेगरी के Expenses यानी खर्चे दिखाए गए हैं।", null)} style={{fontSize:'10px', fontWeight:'400', color:'#64748B', marginBottom:'8px', letterSpacing:'0.5px'}}>
    OPERATING EXPENSES
  </div>
  
  {Object.keys(groupedExpenses).length === 0 && <PnLRow label="No expenses recorded" amount="-" isSub />}
  
  {Object.entries(groupedExpenses).map(([category, amount]) => (
     <PnLRow key={category} label={category} amount={`(${formatCurrency(amount)})`} isSub voiceText={`${category} में आपने लगभग ${formatVoiceCurrency(amount)} खर्च किए हैं।`} handleVoiceClick={handleVoiceClick} />
  ))}
  
  <div style={{borderBottom:'1px solid #e2e8f0', marginBottom:'16px', paddingBottom:'4px'}}>
      <PnLRow 
          label="Total Operating Exp." 
          amount={`(${formatCurrency(financials.expense)})`} 
          color="#DC2626" /* RED COLOR FOR EXPENSE */
          isTotal 
          icon={<TrendingDown size={16} color="#DC2626" strokeWidth={2.5} />} /* EXPENSE ICON */
          voiceText={` इन सभी Expenses यानी खर्चों का मिलाकर आपका Total Expense यानी Total खर्चा ${formatVoiceCurrency(financials.expense)} बनता है।`} 
          handleVoiceClick={handleVoiceClick} 
      />
  </div>
  
  {/* --- NET PROFIT / LOSS SECTION (HIGHLIGHTED BORDER) --- */}
  <div style={{
      background: financials.profit >= 0 ? 'rgba(22, 163, 74, 0.05)' : 'rgba(220, 38, 38, 0.05)',
      border: financials.profit >= 0 ? '1px dashed #16A34A' : '1px dashed #DC2626',
      borderRadius: '12px',
      padding: '4px 12px',
      marginTop: '8px'
  }}>
    <PnLRow 
        label={financials.profit >= 0 ? "NET PROFIT" : "NET LOSS"} 
        amount={formatCurrency(financials.profit)} 
        isTotal 
        color={financials.profit >= 0 ? "#16A34A" : "#DC2626"} /* DYNAMIC COLOR */
        icon={<Wallet size={16} color={financials.profit >= 0 ? "#16A34A" : "#DC2626"} strokeWidth={2.5}/>} /* DYNAMIC ICON */
        voiceText={` आख़िर में आपका नेट ${financials.profit >= 0 ? 'फायदा' : 'नुकसान'} ${formatVoiceCurrency(Math.abs(financials.profit))} निकला है।`} 
        handleVoiceClick={handleVoiceClick} 
    />
  </div>
</div>
                  </div>
                </div>
            </>
        )}

        {/* LEDGER TAB (UPDATED WITH CLICK ACTIONS & TOTALS) */}
        {activeTab === 'ledger' && (() => {
            const totalRecAmount = ledger.receivables.reduce((sum, item) => sum + item.amount, 0);
            const totalPayAmount = ledger.payables.reduce((sum, item) => sum + item.amount, 0);
            
            return (
            <div style={{display:'flex', flexDirection:'column', gap:'16px'}}>
                <div id="ledger-receivables" style={styles.card}>  {/* 👇 यहाँ ID लगा दी */}
                    <div 
                        onClick={(e) => handleVoiceClick(e, `आपको Total ${ledger.receivables.length} पार्टी से Total ${formatVoiceCurrency(totalRecAmount)} लेने हैं।`, null)}
                        style={{padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.3)', background:'rgba(255,255,255,0.2)', cursor:'pointer', borderTopLeftRadius: '24px', borderTopRightRadius: '24px'}}
                    >
                        <h3 style={{margin:0, fontSize:'13px', color:'#1E40AF', fontWeight:'400'}}>Amount to Receive ( रुपये Lene Baki Hain)</h3>
                    </div>
                    <div style={{padding:'10px'}}>
                        {ledger.receivables.length === 0 && <p style={{textAlign:'center', fontSize:'11px', color:'#555', padding:'20px', fontWeight:'600'}}>No pending payments.</p>}
                        {ledger.receivables.map((r, i) => (
                            <div key={i} onClick={(e) => handleVoiceClick(e, `${r.party} से ${r.type} के ${formatVoiceCurrency(r.amount)} लेने हैं।`, null)} style={{...styles.listItem, cursor:'pointer'}}>
                                <div>
                                   <div style={{fontWeight:'600', fontSize:'13px', color:'#0F172A', letterSpacing:'-0.2px'}}>{r.party} <StatusBadge status={r.status} small/></div>
<div style={{fontSize:'10px', color:'#64748B', marginTop:'2px', fontWeight:'400'}}>{formatDate(r.date)} • {r.type}</div>
                                </div>
                                <div style={{color:'#16A34A', fontWeight:'600', fontSize:'14px'}}>{formatCurrency(r.amount)}</div>
                            </div>
                        ))}
                    </div>
                    {/* TOTAL RECEIVABLES SECTION */}
                    {ledger.receivables.length > 0 && (
                        <div style={{padding: '12px 20px', borderTop: '1px dashed rgba(255,255,255,0.4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(22, 163, 74, 0.05)', borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px'}}>
                            <span style={{fontSize: '12px', fontWeight: '400', color: '#111'}}>Total Lene Hain:</span>
                            <span style={{fontSize: '15px', fontWeight: '600', color: '#16A34A'}}>{formatCurrency(totalRecAmount)}</span>
                        </div>
                    )}
                </div>

                <div id="ledger-payables" style={styles.card}>   
                    <div 
                        onClick={(e) => handleVoiceClick(e, ` आपको Total ${ledger.payables.length} पार्टी को Total ${formatVoiceCurrency(totalPayAmount)} देने हैं।`, null)}
                        style={{padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.3)', background:'rgba(255, 0, 0, 0.05)', cursor:'pointer', borderTopLeftRadius: '24px', borderTopRightRadius: '24px'}}
                    >
                        <h3 style={{margin:0, fontSize:'13px', color:'#991B1B', fontWeight:'400'}}> Amount to Pay ( रुपये Dene Baki Hain)</h3>
                    </div>
                    <div style={{padding:'10px'}}>
                        {ledger.payables.length === 0 && <p style={{textAlign:'center', fontSize:'11px', color:'#555', padding:'20px', fontWeight:'600'}}>No pending payments.</p>}
                        {ledger.payables.map((p, i) => (
                            <div key={i} onClick={(e) => handleVoiceClick(e, `${p.party} को ${p.type} के ${formatVoiceCurrency(p.amount)} देने हैं।`, null)} style={{...styles.listItem, cursor:'pointer'}}>
                                <div>
                                    <div style={{fontWeight:'400', fontSize:'13px', color:'#111'}}>{p.party} <StatusBadge status={p.status} small/></div>
                                    <div style={{fontSize:'10px', color:'#444', marginTop:'4px', fontWeight:'400'}}>{formatDate(p.date)} • {p.type}</div>
                                </div>
                                <div style={{color:'#DC2626', fontWeight:'600', fontSize:'14px'}}>{formatCurrency(p.amount)}</div>
                            </div>
                        ))}
                    </div>
                    {/* TOTAL PAYABLES SECTION */}
                    {ledger.payables.length > 0 && (
                        <div style={{padding: '12px 20px', borderTop: '1px dashed rgba(255,255,255,0.4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(220, 38, 38, 0.05)', borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px'}}>
                            <span style={{fontSize: '12px', fontWeight: '400', color: '#111'}}>Total Dene Hain:</span>
                            <span style={{fontSize: '15px', fontWeight: '600', color: '#DC2626'}}>{formatCurrency(totalPayAmount)}</span>
                        </div>
                    )}
                </div>
            </div>
            );
        })()}

      </div>

     {/* ===== 🌟 PREMIUM SOLID WHITE SMART GUIDE MODAL 🌟 ===== */}
      {showGuideModal && (
          <div style={{...styles.modalOverlay, zIndex: 10001}}>
              <div style={{...styles.modalBox, background: '#ffffff', borderRadius: '24px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', border: '1px solid #e2e8f0'}}>
                  
                  {/* Premium Header */}
                  <div style={{...styles.modalHeader, background: '#f8fafc', borderBottom: '1px solid #f1f5f9', padding: '16px 20px', borderRadius: '24px 24px 0 0'}}>
                      <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                          {/* Colorful Gradient Icon for Guide */}
                          <div style={{ background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', padding: '6px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.15)' }}>
                              <HelpCircle size={18} color="#0284C7"/>
                          </div>
                          <h3 style={{fontSize: '15px', fontWeight: '600', color: '#0F172A', margin: 0, letterSpacing: '0.3px'}}>Smart Live Guide</h3>
                      </div>
                      
                      {/* 👇 यहाँ आवाज़ तुरंत बंद करने का 100% पक्का कोड लगाया गया है 👇 */}
                      <button onClick={(e) => {
                          if (e) { e.preventDefault(); e.stopPropagation(); }
                          
                          // आवाज़ को तुरंत रोकने का कोड
                          if (window.speechSynthesis) {
                              window.speechSynthesis.pause();  // पहले पॉज़ करें
                              window.speechSynthesis.cancel(); // फिर पूरी तरह कैंसल कर दें
                          }
                          
                          setShowGuideModal(false); // मोडल को तुरंत क्लोज करें
                          
                          if (typeof closeModalsVoice === 'function') {
                              closeModalsVoice(e);
                          }
                      }} style={{...styles.closeBtn, background: '#ffffff', border: '1px solid #e2e8f0', width: '32px', height: '32px'}}>
                          <X size={16} color="#64748B" strokeWidth={2.5}/>
                      </button>
                  </div>

                  <div style={{padding:'20px'}}>
                      <p style={{fontSize:'10px', fontWeight:'400', color:'#64748B', marginBottom:'16px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Aap kis topic ke bare me sikhna chahte hain? Niche Diye Gaye Options Me Se Chune.</p>
                      
                      <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                          {/* Guide Item 1 */}
                          <div onClick={() => startSmartGuide('yarn_purchase')} style={{display: 'flex', justifyContent: 'space-between', padding: '14px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s'}}>
                              <span style={{fontSize:'11px', fontWeight:'600', color:'#0F172A'}}>1. Yarn Purchase (Yani Dhaga Kharidi) Ki Entry Kaise Add Kare?</span>
                              <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                                  <PlayCircle size={16} color="#4338CA"/>
                              </div>
                          </div>
                          
                          {/* Guide Item 2 */}
                          <div onClick={() => startSmartGuide('revenue')} style={{display: 'flex', justifyContent: 'space-between', padding: '14px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s'}}>
                              <span style={{fontSize:'11px', fontWeight:'600', color:'#0F172A'}}>2. Revenue (Yani Aamdani) Ki Entry Kaise Add Kare?</span>
                              <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                                  <PlayCircle size={16} color="#4338CA"/>
                              </div>
                          </div>
                                
                          {/* Guide Item 3 */}
                          <div onClick={() => startSmartGuide('report')} style={{display: 'flex', justifyContent: 'space-between', padding: '14px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s'}}>
                              <span style={{fontSize:'11px', fontWeight:'600', color:'#0F172A'}}>3. Financial Report Ki PDF File Kaise Download Kare?</span>
                              <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                                  <PlayCircle size={16} color="#4338CA"/>
                              </div>
                          </div>

                          {/* Guide Item 4 */}
                          <div onClick={() => startSmartGuide('ledger')} style={{display: 'flex', justifyContent: 'space-between', padding: '14px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s'}}>
                              <span style={{fontSize:'11px', fontWeight:'600', color:'#0F172A'}}>4. Party Ledger Me Kis Ko Kitna Paisa Dena Baki Hain (Amount To Pay) Aur Kisse Kitna Paisa Lena Baki Hain (Amount To Recieve) Kaise Check Kare?</span> 
                              <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                                  <PlayCircle size={16} color="#4338CA"/>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* ===== 🌟 PREMIUM ADD EXPENSE MODAL 🌟 ===== */}
      {activeModal === 'expense' && (
        <div style={{...styles.modalOverlay, zIndex: 9999}}>
          <div style={{...styles.modalBox, background: '#ffffff', borderRadius: '24px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', border: '1px solid #e2e8f0'}}>
              
              <div style={{...styles.modalHeader, background: '#f8fafc', borderBottom: '1px solid #f1f5f9', padding: '16px 20px', borderRadius: '24px 24px 0 0'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                      <div style={{ background: 'linear-gradient(135deg, #FEE2E2, #FCA5A5)', padding: '6px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(220, 38, 38, 0.15)' }}>
                          <TrendingDown size={18} color="#DC2626"/>
                      </div>
                      <h3 style={{fontSize: '16px', fontWeight: '600', color: '#0F172A', margin: 0, letterSpacing: '-0.5px'}}>Add Expense</h3>
                  </div>
                  <button onClick={closeModalsVoice} style={{...styles.closeBtn, background: '#ffffff', border: '1px solid #e2e8f0', width: '32px', height: '32px'}}>
                      <X size={16} color="#64748B" strokeWidth={2.5}/>
                  </button>
              </div>

              <div style={{padding:'20px'}}>
                <div style={{ display: 'flex', background: '#f8fafc', padding: '4px', borderRadius: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                    <button 
                        id="tab-general-expense" 
                        onClick={(e) => handleVoiceClick(e, " जनरल Button हैं।", () => setExpenseType('general'), 'tab-general-expense', expenseType === 'general', " आप पहले से ही जनरल Button में मौजूद हैं।")} 
                        style={{
                            flex: 1, padding: '8px 6px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                            background: expenseType === 'general' ? '#ffffff' : 'transparent',
                            color: expenseType === 'general' ? '#0F172A' : '#64748B',
                            border: expenseType === 'general' ? '1px solid #e2e8f0' : '1px solid transparent',
                            boxShadow: expenseType === 'general' ? '0 4px 10px rgba(0,0,0,0.03)' : 'none'
                        }}>
                        General
                    </button>
                    <button 
                        id="tab-yarn-expense" 
                        onClick={(e) => handleVoiceClick(e, " यार्न परचेस Button हैं।", () => setExpenseType('stock'), 'tab-yarn-expense', expenseType === 'stock', " आप पहले से ही यार्न परचेस Button में मौजूद हैं।")} 
                        style={{
                            flex: 1, padding: '8px 6px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                            background: expenseType === 'stock' ? '#ffffff' : 'transparent',
                            color: expenseType === 'stock' ? '#0F172A' : '#64748B',
                            border: expenseType === 'stock' ? '1px solid #e2e8f0' : '1px solid transparent',
                            boxShadow: expenseType === 'stock' ? '0 4px 10px rgba(0,0,0,0.03)' : 'none'
                        }}>
                        Yarn Purchase
                    </button>
                </div>

                {expenseType === 'general' ? (
                  <form onSubmit={handleExpenseSubmit} style={styles.form}>
                     <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                         {expenseForm.category === 'Salaries' && (
                           <div style={{background:'#f8fafc', padding:'12px', borderRadius:'14px', marginBottom:'5px', border:'1px solid #e2e8f0'}}>
                              <div style={{fontSize:'10px', color:'#64748B', marginBottom:'6px', fontWeight:'400', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Monthly Production: {monthlyProduction} Meters</div>
                              <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                  <span style={{fontSize:'11px', color:'#0F172A', fontWeight:'400'}}>Rate/M: ₹</span>
                                  <input 
                                      type="number" 
                                      value={workerRate} 
                                      onChange={e => setWorkerRate(e.target.value)} 
                                      onFocus={() => handleFocusVoice("यहाँ एक मीटर कपड़ा बनाने का वर्कर का रेट सेट करें।")}
                                      onBlur={e => handleTextBlurVoice(e.target.value, "वर्कर का रेट")}
                                      style={{...styles.input, width:'80px', padding:'8px 10px', background: '#ffffff', border: '1px solid #e2e8f0'}} 
                                  />
                              </div>
                           </div>
                         )}
                         
                        <div style={styles.row}>
                          <input 
                              id="input-exp-date"
                              type="date" 
                              value={expenseForm.date} 
                              onChange={e => setExpenseForm({...expenseForm, date: e.target.value})} 
                              onFocus={() => handleFocusVoice(" यहाँ खर्चे की तारीख चुनें।")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                          <select 
                              id="select-exp-cat"
                              value={expenseForm.category} 
                              onFocus={() => handleFocusVoice("यहाँ खर्चे का प्रकार चुनें, जैसे लाइट बिल या भाड़ा।")}
                              onChange={e => {
                                  const sel = expenseCategories.find(c => c.value === e.target.value);
                                  setExpenseForm({...expenseForm, category: e.target.value, amount: sel?.autoValue ? sel.autoValue : ""})
                                  handleSelectVoice(e.target.value, "खर्चे का प्रकार");
                              }} 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                          >
                              <option value="" disabled>Category</option>
                              {expenseCategories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        {expenseForm.category === "Other" && (
                            <input 
                                placeholder="Specify Name..." 
                                value={expenseForm.customCategory} 
                                onChange={e => setExpenseForm({...expenseForm, customCategory: e.target.value})} 
                                onFocus={() => handleFocusVoice(" यहाँ अपने खर्चे का नाम खुद लिखें।")}
                                onBlur={(e) => handleTextBlurVoice(e.target.value, "खर्चे का नाम")}
                                style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                            />
                        )}
                        
                        <input 
                            id="input-exp-amount"
                            type="number" 
                            placeholder="Amount (₹)" 
                            value={expenseForm.amount} 
                            onChange={e => setExpenseForm({...expenseForm, amount: e.target.value})} 
                            onFocus={() => handleFocusVoice(" यहाँ खर्चे की रकम भरिये।")}
                            onBlur={(e) => handleTextBlurVoice(e.target.value, "खर्चे की रकम")}
                            style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                        />
                        
                        <div style={styles.row}>
                          <input 
                              id="input-exp-party"
                              type="text" 
                              placeholder="Paid To (Party Name)" 
                              value={expenseForm.party} 
                              onChange={e => setExpenseForm({...expenseForm, party: e.target.value})} 
                              onFocus={() => handleFocusVoice(" यहाँ उस पार्टी का नाम लिखें जिसको पैसे दिए हैं।")}
                              onBlur={(e) => handleTextBlurVoice(e.target.value, "पार्टी का नाम")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                          <select 
                              id="select-exp-status"
                              value={expenseForm.status} 
                              onFocus={() => handleFocusVoice(" यहाँ चुनें कि पैसे दे दिए हैं या अभी बाकी हैं।")}
                              onChange={e => {
                                  setExpenseForm({...expenseForm, status: e.target.value});
                                  handleSelectVoice(e.target.value, "Status");
                              }} 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                          >
                              <option value="Paid">Paid</option>
                              <option value="Pending">Pending</option>
                          </select>
                        </div>
                     </div>
                     <button id="btn-save-expense" type="submit" style={{
                        width: '100%', background: 'linear-gradient(135deg, #EF4444, #DC2626)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px'
                     }}>
                        <CheckCircle size={18} color="white"/> Save Expense
                     </button>
                  </form>
                ) : (
                  <form onSubmit={handleStockSubmit} style={styles.form}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                        <div style={styles.row}>
                          <input 
                              id="input-yarn-date"
                              type="date" 
                              value={stockForm.date} 
                              onChange={e => setStockForm({...stockForm, date: e.target.value})} 
                              onFocus={() => handleFocusVoice(" यहाँ Yarn यानी धागा आने की तारीख चुनें।")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                          <select 
                              id="select-yarn-material"
                              value={stockForm.material} 
                              onFocus={() => handleFocusVoice(" यहाँ Yarn यानी धागे का मटेरियल चुनें।")}
                              onChange={e => {
                                  setStockForm({...stockForm, material: e.target.value});
                                  handleSelectVoice(e.target.value, "धागे का मटेरियल");
                              }} 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                          >
                              <option value="" disabled>Material</option>
                              {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        <div style={styles.row}>
                          <select 
                              id="select-yarn-type"
                              value={stockForm.type} 
                              onFocus={() => handleFocusVoice(" यहाँ Yarn (धागे) का Type चुनें।")}
                              onChange={e => {
                                  setStockForm({...stockForm, type: e.target.value});
                                  handleSelectVoice(e.target.value, " Yarn (धागे) का Type");
                              }} 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                          >
                              <option value="" disabled>Type</option>
                              {FABRIC_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        <div style={styles.row}>
                          <input 
                              id="input-yarn-qty"
                              type="number" 
                              placeholder="Qty (kg)" 
                              value={stockForm.qty} 
                              onChange={e => setStockForm({...stockForm, qty: e.target.value})} 
                              onFocus={() => handleFocusVoice(" यहाँ कितना किलो Yarn यानी धागा ख़रीदा, वो लिखें।")}
                              onBlur={(e) => handleTextBlurVoice(e.target.value, " किलो")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                          <input 
                              id="input-yarn-price"
                              type="number" 
                              placeholder="Price" 
                              value={stockForm.price} 
                              onChange={e => setStockForm({...stockForm, price: e.target.value})} 
                              onFocus={() => handleFocusVoice(" यहाँ Yarn (धागे) का रेट लिखें।")}
                              onBlur={(e) => handleTextBlurVoice(e.target.value, " रेट")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                        </div>
                    </div>
                    <button id="btn-save-yarn" type="submit" style={{
                        width: '100%', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px'
                    }}>
                       <CheckCircle size={18} color="white"/> Save Stock
                    </button>
                  </form>
                )}
              </div>
          </div>
        </div>
      )}

      {/* ===== 🌟 PREMIUM ADD REVENUE MODAL 🌟 ===== */}
      {activeModal === 'revenue' && (
        <div style={{...styles.modalOverlay, zIndex: 9999}}>
          <div style={{...styles.modalBox, background: '#ffffff', borderRadius: '24px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', border: '1px solid #e2e8f0'}}>
              
              <div style={{...styles.modalHeader, background: '#f8fafc', borderBottom: '1px solid #f1f5f9', padding: '16px 20px', borderRadius: '24px 24px 0 0'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                      <div style={{ background: 'linear-gradient(135deg, #D1FAE5, #6EE7B7)', padding: '6px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(5, 150, 105, 0.15)' }}>
                          <TrendingUp size={18} color="#059669"/>
                      </div>
                      <h3 style={{fontSize: '16px', fontWeight: '600', color: '#0F172A', margin: 0, letterSpacing: '-0.5px'}}>Add Revenue</h3>
                  </div>
                  <button onClick={closeModalsVoice} style={{...styles.closeBtn, background: '#ffffff', border: '1px solid #e2e8f0', width: '32px', height: '32px'}}>
                      <X size={16} color="#64748B" strokeWidth={2.5}/>
                  </button>
              </div>

              <div style={{padding:'20px'}}>
                  <form onSubmit={handleRevenueSubmit} style={styles.form}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                        <div style={styles.row}>
                          <input 
                              id="input-rev-date"
                              type="date" 
                              value={revenueForm.date} 
                              onChange={e => setRevenueForm({...revenueForm, date: e.target.value})} 
                              onFocus={() => handleFocusVoice(" यहाँ बेचने की तारीख चुनें।")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                          <select 
                              id="select-rev-fabric"
                              value={revenueForm.fabric} 
                              onFocus={() => handleFocusVoice(" यहाँ कपड़े का प्रकार चुनें।")}
                              onChange={e => {
                                  setRevenueForm({...revenueForm, fabric: e.target.value});
                                  handleSelectVoice(e.target.value, " कपड़े का प्रकार");
                              }} 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                          >
                              <option value="" disabled>Fabric</option>
                              {REVENUE_FABRIC_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </div>
                        <div style={styles.row}>
                          <select 
                              id="select-rev-material"
                              value={revenueForm.material} 
                              onFocus={() => handleFocusVoice(" यहाँ मटेरियल चुनें।")}
                              onChange={e => {
                                  setRevenueForm({...revenueForm, material: e.target.value});
                                  handleSelectVoice(e.target.value, " मटेरियल");
                              }} 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                          >
                              <option value="" disabled>Material</option>
                              {REVENUE_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <input 
                              id="input-rev-rate"
                              type="number" 
                              placeholder="Rate (₹)" 
                              value={revenueForm.rate} 
                              onChange={e => setRevenueForm({...revenueForm, rate: e.target.value})} 
                              onFocus={() => handleFocusVoice(" यहाँ कपड़े का रेट लिखें।")}
                              onBlur={(e) => handleTextBlurVoice(e.target.value, "रेट")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                        </div>
                        <input 
                            id="input-rev-qty"
                            type="number" 
                            placeholder="Meters Sold" 
                            value={revenueForm.qty} 
                            onChange={e => setRevenueForm({...revenueForm, qty: e.target.value})} 
                            onFocus={() => handleFocusVoice(" यहाँ कितना मीटर कपड़ा बेचा, वो लिखें।")}
                            onBlur={(e) => handleTextBlurVoice(e.target.value, "मीटर")}
                            style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                        />
                        
                        <div style={styles.row}>
                          <input 
                              id="input-rev-party"
                              type="text" 
                              placeholder="Sold To (Party Name)" 
                              value={revenueForm.party} 
                              onChange={e => setRevenueForm({...revenueForm, party: e.target.value})} 
                              onFocus={() => handleFocusVoice(" यहाँ उस पार्टी का नाम लिखें जिसको कपड़ा बेचा है।")}
                              onBlur={(e) => handleTextBlurVoice(e.target.value, "पार्टी का नाम")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                          <select 
                              id="select-rev-status"
                              value={revenueForm.status} 
                              onFocus={() => handleFocusVoice(" यहाँ चुनें कि पैसे मिल गए हैं या अभी उधार है।")}
                              onChange={e => {
                                  setRevenueForm({...revenueForm, status: e.target.value});
                                  handleSelectVoice(e.target.value, "Status");
                              }} 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                          >
                              <option value="Paid">Paid</option>
                              <option value="Pending">Pending</option>
                          </select>
                        </div>
                    </div>
                    <button id="btn-save-revenue" type="submit" style={{
                        width: '100%', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px'
                    }}>
                        <CheckCircle size={18} color="white"/> Save Revenue
                    </button>
                  </form>
              </div>
          </div>
        </div>
      )}

     {/* ===== 🌟 PREMIUM SMART GUIDE MODAL 🌟 ===== */}
      <Modal
          isOpen={showGuideModal}
          onClose={() => {
              if (window.speechSynthesis) {
                  window.speechSynthesis.pause();
                  window.speechSynthesis.cancel();
              }
              setShowGuideModal(false);
              if (typeof closeModalsVoice === 'function') closeModalsVoice();
          }}
          title="Smart Live Guide"
          icon={HelpCircle}
          maxWidth="430px"
      >
          <div>
              <p style={{fontSize:'10px', fontWeight:'400', color:'#64748B', marginBottom:'16px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Aap kis topic ke bare me sikhna chahte hain? Niche Diye Gaye Options Me Se Chune.</p>
              
              <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                  <div onClick={() => startSmartGuide('yarn_purchase')} style={{display: 'flex', justifyContent: 'space-between', padding: '14px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'11px', fontWeight:'600', color:'#0F172A'}}>1. Yarn Purchase (Yani Dhaga Kharidi) Ki Entry Kaise Add Kare?</span>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={16} color="#4338CA"/>
                      </div>
                  </div>
                  <div onClick={() => startSmartGuide('revenue')} style={{display: 'flex', justifyContent: 'space-between', padding: '14px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'11px', fontWeight:'600', color:'#0F172A'}}>2. Revenue (Yani Aamdani) Ki Entry Kaise Add Kare?</span>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={16} color="#4338CA"/>
                      </div>
                  </div>
                  <div onClick={() => startSmartGuide('report')} style={{display: 'flex', justifyContent: 'space-between', padding: '14px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'11px', fontWeight:'600', color:'#0F172A'}}>3. Financial Report Ki PDF File Kaise Download Kare?</span>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={16} color="#4338CA"/>
                      </div>
                  </div>
                  <div onClick={() => startSmartGuide('ledger')} style={{display: 'flex', justifyContent: 'space-between', padding: '14px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'11px', fontWeight:'600', color:'#0F172A'}}>4. Party Ledger Me Kis Ko Kitna Paisa Dena Baki Hain (Amount To Pay) Aur Kisse Kitna Paisa Lena Baki Hain (Amount To Recieve) Kaise Check Kare?</span> 
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={16} color="#4338CA"/>
                      </div>
                  </div>
              </div>
          </div>
      </Modal>

      {/* ===== 🌟 PREMIUM ADD EXPENSE MODAL 🌟 ===== */}
      <Modal isOpen={activeModal === 'expense'} onClose={closeModalsVoice} title="Add Expense" icon={TrendingDown} iconColor="#DC2626" iconBg="linear-gradient(135deg, #FEE2E2, #FCA5A5)">
        <div>
          <div style={{ display: 'flex', background: '#f8fafc', padding: '4px', borderRadius: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
              <button 
                  id="tab-general-expense" 
                  onClick={(e) => handleVoiceClick(e, " जनरल Button हैं।", () => setExpenseType('general'), 'tab-general-expense', expenseType === 'general', " आप पहले से ही जनरल Button में मौजूद हैं।")} 
                  style={{
                      flex: 1, padding: '8px 6px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                      background: expenseType === 'general' ? '#ffffff' : 'transparent',
                      color: expenseType === 'general' ? '#0F172A' : '#64748B',
                      border: expenseType === 'general' ? '1px solid #e2e8f0' : '1px solid transparent',
                      boxShadow: expenseType === 'general' ? '0 4px 10px rgba(0,0,0,0.03)' : 'none'
                  }}>
                  General
              </button>
              <button 
                  id="tab-yarn-expense" 
                  onClick={(e) => handleVoiceClick(e, " यार्न परचेस Button हैं।", () => setExpenseType('stock'), 'tab-yarn-expense', expenseType === 'stock', " आप पहले से ही यार्न परचेस Button में मौजूद हैं।")} 
                  style={{
                      flex: 1, padding: '8px 6px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                      background: expenseType === 'stock' ? '#ffffff' : 'transparent',
                      color: expenseType === 'stock' ? '#0F172A' : '#64748B',
                      border: expenseType === 'stock' ? '1px solid #e2e8f0' : '1px solid transparent',
                      boxShadow: expenseType === 'stock' ? '0 4px 10px rgba(0,0,0,0.03)' : 'none'
                  }}>
                  Yarn Purchase
              </button>
          </div>

          {expenseType === 'general' ? (
            <form onSubmit={handleExpenseSubmit} style={styles.form}>
               <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                   {expenseForm.category === 'Salaries' && (
                     <div style={{background:'#f8fafc', padding:'12px', borderRadius:'14px', marginBottom:'5px', border:'1px solid #e2e8f0'}}>
                        <div style={{fontSize:'10px', color:'#64748B', marginBottom:'6px', fontWeight:'400', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Monthly Production: {monthlyProduction} Meters</div>
                        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <span style={{fontSize:'11px', color:'#0F172A', fontWeight:'400'}}>Rate/M: ₹</span>
                            <input 
                                type="number" 
                                value={workerRate} 
                                onChange={e => setWorkerRate(e.target.value)} 
                                onFocus={() => handleFocusVoice(" यहाँ एक मीटर कपड़ा बनाने का वर्कर का रेट सेट करें।")}
                                onBlur={e => handleTextBlurVoice(e.target.value, "वर्कर का रेट")}
                                style={{...styles.input, width:'80px', padding:'8px 10px', background: '#ffffff', border: '1px solid #e2e8f0'}} 
                            />
                        </div>
                     </div>
                   )}
                   
                  <div style={styles.row}>
                    <input 
                        id="input-exp-date"
                        type="date" 
                        value={expenseForm.date} 
                        onChange={e => setExpenseForm({...expenseForm, date: e.target.value})} 
                        onFocus={() => handleFocusVoice(" यहाँ खर्चे की तारीख चुनें।")}
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                    />
                    <select 
                        id="select-exp-cat"
                        value={expenseForm.category} 
                        onFocus={() => handleFocusVoice(" यहाँ खर्चे का प्रकार चुनें, जैसे लाइट बिल या भाड़ा।")}
                        onChange={e => {
                            const sel = expenseCategories.find(c => c.value === e.target.value);
                            setExpenseForm({...expenseForm, category: e.target.value, amount: sel?.autoValue ? sel.autoValue : ""})
                            handleSelectVoice(e.target.value, " खर्चे का प्रकार");
                        }} 
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                    >
                        <option value="" disabled>Category</option>
                        {expenseCategories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  {expenseForm.category === "Other" && (
                      <input 
                          placeholder="Specify Name..." 
                          value={expenseForm.customCategory} 
                          onChange={e => setExpenseForm({...expenseForm, customCategory: e.target.value})} 
                          onFocus={() => handleFocusVoice(" यहाँ अपने खर्चे का नाम खुद लिखें।")}
                          onBlur={(e) => handleTextBlurVoice(e.target.value, " खर्चे का नाम")}
                          style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                      />
                  )}
                  
                  <input 
                      id="input-exp-amount"
                      type="number" 
                      placeholder="Amount (₹)" 
                      value={expenseForm.amount} 
                      onChange={e => setExpenseForm({...expenseForm, amount: e.target.value})} 
                      onFocus={() => handleFocusVoice(" यहाँ खर्चे की रकम भरिये।")}
                      onBlur={(e) => handleTextBlurVoice(e.target.value, " खर्चे की रकम")}
                      style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                  />
                  
                  <div style={styles.row}>
                    <input 
                        id="input-exp-party"
                        type="text" 
                        placeholder="Paid To (Party Name)" 
                        value={expenseForm.party} 
                        onChange={e => setExpenseForm({...expenseForm, party: e.target.value})} 
                        onFocus={() => handleFocusVoice(" यहाँ उस पार्टी का नाम लिखें जिसको पैसे दिए हैं।")}
                        onBlur={(e) => handleTextBlurVoice(e.target.value, " पार्टी का नाम")}
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                    />
                    <select 
                        id="select-exp-status"
                        value={expenseForm.status} 
                        onFocus={() => handleFocusVoice(" यहाँ चुनें कि पैसे दे दिए हैं या अभी बाकी हैं।")}
                        onChange={e => {
                            setExpenseForm({...expenseForm, status: e.target.value});
                            handleSelectVoice(e.target.value, "Status");
                        }} 
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                    >
                        <option value="Paid">Paid</option>
                        <option value="Pending">Pending</option>
                    </select>
                  </div>
               </div>
               <button id="btn-save-expense" type="submit" style={{
                  width: '100%', background: 'linear-gradient(135deg, #EF4444, #DC2626)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px'
               }}>
                  <CheckCircle size={18} color="white"/> Save Expense
               </button>
            </form>
          ) : (
            <form onSubmit={handleStockSubmit} style={styles.form}>
              <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  <div style={styles.row}>
                    <input 
                        id="input-yarn-date"
                        type="date" 
                        value={stockForm.date} 
                        onChange={e => setStockForm({...stockForm, date: e.target.value})} 
                        onFocus={() => handleFocusVoice(" यहाँ Yarn यानी धागा आने की तारीख चुनें।")}
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                    />
                    <select 
                        id="select-yarn-material"
                        value={stockForm.material} 
                        onFocus={() => handleFocusVoice(" यहाँ Yarn यानी धागे का मटेरियल चुनें।")}
                        onChange={e => {
                            setStockForm({...stockForm, material: e.target.value});
                            handleSelectVoice(e.target.value, "धागे का मटेरियल");
                        }} 
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                    >
                        <option value="" disabled>Material</option>
                        {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div style={styles.row}>
                    <select 
                        id="select-yarn-type"
                        value={stockForm.type} 
                        onFocus={() => handleFocusVoice(" यहाँ Yarn (धागे) का Type चुनें।")}
                        onChange={e => {
                            setStockForm({...stockForm, type: e.target.value});
                            handleSelectVoice(e.target.value, " Yarn (धागे) का Type");
                        }} 
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                    >
                        <option value="" disabled>Type</option>
                        {FABRIC_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div style={styles.row}>
                    <input 
                        id="input-yarn-qty"
                        type="number" 
                        placeholder="Qty (kg)" 
                        value={stockForm.qty} 
                        onChange={e => setStockForm({...stockForm, qty: e.target.value})} 
                        onFocus={() => handleFocusVoice(" यहाँ कितना किलो Yarn यानी धागा ख़रीदा, वो लिखें।")}
                        onBlur={(e) => handleTextBlurVoice(e.target.value, " किलो")}
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                    />
                    <input 
                        id="input-yarn-price"
                        type="number" 
                        placeholder="Price" 
                        value={stockForm.price} 
                        onChange={e => setStockForm({...stockForm, price: e.target.value})} 
                        onFocus={() => handleFocusVoice(" यहाँ Yarn (धागे) का रेट लिखें।")}
                        onBlur={(e) => handleTextBlurVoice(e.target.value, " रेट")}
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                    />
                  </div>
              </div>
              <button id="btn-save-yarn" type="submit" style={{
                  width: '100%', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px'
              }}>
                 <CheckCircle size={18} color="white"/> Save Stock
              </button>
            </form>
          )}
        </div>
      </Modal>

      {/* ===== 🌟 PREMIUM ADD REVENUE MODAL 🌟 ===== */}
      <Modal isOpen={activeModal === 'revenue'} onClose={closeModalsVoice} title="Add Revenue" icon={TrendingUp} iconColor="#059669" iconBg="linear-gradient(135deg, #D1FAE5, #6EE7B7)">
          <div>
              <form onSubmit={handleRevenueSubmit} style={styles.form}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                    <div style={styles.row}>
                      <input 
                          id="input-rev-date"
                          type="date" 
                          value={revenueForm.date} 
                          onChange={e => setRevenueForm({...revenueForm, date: e.target.value})} 
                          onFocus={() => handleFocusVoice(" यहाँ बेचने की तारीख चुनें।")}
                          style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                      />
                      <select 
                          id="select-rev-fabric"
                          value={revenueForm.fabric} 
                          onFocus={() => handleFocusVoice(" यहाँ कपड़े का प्रकार चुनें।")}
                          onChange={e => {
                              setRevenueForm({...revenueForm, fabric: e.target.value});
                              handleSelectVoice(e.target.value, " कपड़े का प्रकार");
                          }} 
                          style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                      >
                          <option value="" disabled>Fabric</option>
                          {REVENUE_FABRIC_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div style={styles.row}>
                      <select 
                          id="select-rev-material"
                          value={revenueForm.material} 
                          onFocus={() => handleFocusVoice(" यहाँ मटेरियल चुनें।")}
                          onChange={e => {
                              setRevenueForm({...revenueForm, material: e.target.value});
                              handleSelectVoice(e.target.value, " मटेरियल");
                          }} 
                          style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                      >
                          <option value="" disabled>Material</option>
                          {REVENUE_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <input 
                          id="input-rev-rate"
                          type="number" 
                          placeholder="Rate (₹)" 
                          value={revenueForm.rate} 
                          onChange={e => setRevenueForm({...revenueForm, rate: e.target.value})} 
                          onFocus={() => handleFocusVoice(" यहाँ कपड़े का रेट लिखें।")}
                          onBlur={(e) => handleTextBlurVoice(e.target.value, "रेट")}
                          style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                      />
                    </div>
                    <input 
                        id="input-rev-qty"
                        type="number" 
                        placeholder="Meters Sold" 
                        value={revenueForm.qty} 
                        onChange={e => setRevenueForm({...revenueForm, qty: e.target.value})} 
                        onFocus={() => handleFocusVoice(" यहाँ कितना मीटर कपड़ा बेचा, वो लिखें।")}
                        onBlur={(e) => handleTextBlurVoice(e.target.value, "मीटर")}
                        style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                    />
                    
                    <div style={styles.row}>
                      <input 
                          id="input-rev-party"
                          type="text" 
                          placeholder="Sold To (Party Name)" 
                          value={revenueForm.party} 
                          onChange={e => setRevenueForm({...revenueForm, party: e.target.value})} 
                          onFocus={() => handleFocusVoice(" यहाँ उस पार्टी का नाम लिखें जिसको कपड़ा बेचा है।")}
                          onBlur={(e) => handleTextBlurVoice(e.target.value, "पार्टी का नाम")}
                          style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                      />
                      <select 
                          id="select-rev-status"
                          value={revenueForm.status} 
                          onFocus={() => handleFocusVoice(" यहाँ चुनें कि पैसे मिल गए हैं या अभी उधार है।")}
                          onChange={e => {
                              setRevenueForm({...revenueForm, status: e.target.value});
                              handleSelectVoice(e.target.value, "Status");
                          }} 
                          style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}}
                      >
                          <option value="Paid">Paid</option>
                          <option value="Pending">Pending</option>
                      </select>
                    </div>
                </div>
                <button id="btn-save-revenue" type="submit" style={{
                    width: '100%', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px'
                }}>
                    <CheckCircle size={18} color="white"/> Save Revenue
                </button>
              </form>
          </div>
      </Modal>

      {/* ===== 🌟 PREMIUM SOLID WHITE SETTINGS MODAL 🌟 ===== */}
      <Modal isOpen={activeModal === 'settings'} onClose={closeModalsVoice} title="Settings" icon={Settings} iconColor="#475569" iconBg="linear-gradient(135deg, #f1f5f9, #cbd5e1)" maxWidth="380px">
          <div>
              <form onSubmit={handleSettingsSubmit}>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                      <div>
                          <label style={{...styles.fieldLabel, color: '#64748B'}}>Opening Production Stock (Meters)</label>
                          <input 
                              type="number" 
                              value={openingGreyStock} 
                              onChange={e => setOpeningGreyStock(e.target.value)} 
                              onFocus={() => handleFocusVoice(" यहाँ आपके पास शुरू में पहले से कितना मीटर कपड़ा रखा है, वो लिखें।")}
                              onBlur={(e) => handleTextBlurVoice(e.target.value, " ओपनिंग कपड़ा Stock")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                      </div>
                      <div>
                          <label style={{...styles.fieldLabel, color: '#64748B'}}>Opening Yarn Stock (KG)</label>
                          <input 
                              type="number" 
                              value={openingYarnStock} 
                              onChange={e => setOpeningYarnStock(e.target.value)} 
                              onFocus={() => handleFocusVoice(" यहाँ आपके पास शुरू में कितना किलो Yarn यानी धागा रखा है, वो लिखें।")}
                              onBlur={(e) => handleTextBlurVoice(e.target.value, " ओपनिंग Yarn Stock")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                      </div>
                      <div>
                          <label style={{...styles.fieldLabel, color: '#64748B'}}>Yarn Consumption (kg per meter)</label>
                          <input 
                              type="number" 
                              value={yarnConsumptionRate} 
                              onChange={e => setYarnConsumptionRate(e.target.value)} 
                              onFocus={() => handleFocusVoice(" यहाँ एक मीटर कपड़ा बनाने में कितना किलो Yarn यानी धागा लगता है, वो सेट करें।")}
                              onBlur={(e) => handleTextBlurVoice(e.target.value, " Yarn यानी धागे की खपत")}
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                          />
                      </div>
                  </div>
                  <button type="submit" onClick={(e) => handleVoiceClick(e, " Settings Save की जा रही हैं।", null)} 
                  style={{
                      width: '100%', background: 'linear-gradient(135deg, #1e293b, #0f172a)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '20px'
                  }}>
                      <CheckCircle size={18} color="white"/> Save Settings
                  </button>
              </form>
          </div>
      </Modal>

      {/* --- VIEW HISTORY MODAL --- */}
      <Modal isOpen={!!viewHistoryType} onClose={closeModalsVoice} title={viewHistoryType === 'expense' ? 'Expense History' : 'Revenue History'} icon={viewHistoryType === 'expense' ? TrendingDown : TrendingUp} iconColor={viewHistoryType === 'expense' ? '#DC2626' : '#059669'} iconBg={viewHistoryType === 'expense' ? 'linear-gradient(135deg, #FEE2E2, #FCA5A5)' : 'linear-gradient(135deg, #D1FAE5, #6EE7B7)'} maxWidth="600px">
          <div>
              <div style={{display:'flex', flexDirection:'column', gap: '10px'}}>
                  {(viewHistoryType === 'expense' ? expenses : revenue).map((item, index) => (
                      <div key={item.id || item.expense_id || item.revenue_id || index} onClick={(e) => {
                          let msg = "";
                          if (viewHistoryType === 'expense') {
                              msg = item.status === 'Pending' 
                                  ? ` ये ${item.category} का Expense यानी खर्चा है, इसके ${formatVoiceCurrency(item.amount)} देने बाकी हैं।` 
                                  : ` ये ${item.category} का Expense यानी खर्चा है, इसके ${formatVoiceCurrency(item.amount)} दे दिए गए हैं।`;
                          } else {
                              let amt = item.ratePerMeter * item.metersSold;
                              msg = item.status === 'Pending' 
                                  ? ` ये ${item.fabricType} की आमदनी है, इसके ${formatVoiceCurrency(amt)} लेने बाकी हैं।` 
                                  : ` ये ${item.fabricType} की आमदनी है, इसके ${formatVoiceCurrency(amt)} मिल गए हैं।`;
                          }
                          handleVoiceClick(e, msg, null);
                      }} style={{
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          padding: '14px', 
                          background: '#f8fafc',
                          borderRadius: '16px',
                          border: '1px solid #e2e8f0',
                          transition: 'all 0.2s',
                          flexShrink: 0,
                          contentVisibility: 'auto', 
                          containIntrinsicSize: '70px', 
                          willChange: 'transform, opacity'
                      }}>
                          <div style={{display:'flex', flexDirection:'column', gap:'4px', flex:1}}>
                              <span style={{fontSize:'9px', color:'#64748B', fontWeight:'400', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                  {formatDate(item.date)}
                              </span>
                              <div style={{fontSize:'13px', fontWeight:'600', color:'#0F172A', display:'flex', alignItems:'center'}}>
                                  {viewHistoryType === 'revenue' ? item.fabricType : item.category}
                                  <StatusBadge status={item.status} small={true} />
                              </div>
                              <div style={{fontSize:'11px', color:'#475569', fontWeight:'600'}}>
                                   {viewHistoryType === 'revenue' ? `${item.metersSold}m • ${item.material}` : (item.party ? item.party : '-')}
                              </div>
                          </div>

                          <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                              <span style={{
                                  fontSize:'14px', 
                                  fontWeight:'600', 
                                  color: viewHistoryType === 'revenue' ? '#16A34A' : '#DC2626',
                                  whiteSpace: 'nowrap'
                              }}>
                                  {formatCurrency(viewHistoryType === 'revenue' ? item.ratePerMeter * item.metersSold : item.amount)}
                              </span>
                              
                              <button 
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      handleVoiceClick(e, " यह डिलीट बटन है, इससे आप इस एंट्री को मिटा सकते हैं।", () => setDeleteConfirmInfo({ id: item.id, type: viewHistoryType }), 'delete-record');
                                  }}
                                 style={{
                                   background:'#ffffff', 
                                   border:'1px solid #e2e8f0', 
                                   cursor:'pointer', 
                                   width:'32px', height:'32px',
                                   borderRadius:'10px', 
                                   display:'flex', 
                                   alignItems:'center', 
                                   justifyContent:'center',
                                   boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                   transition: 'all 0.2s',
                                   }}
                                 > 
                                  <Trash2 size={14} color="#EF4444"/>
                              </button>
                          </div>
                      </div>
                  ))}
                  {(viewHistoryType === 'expense' ? expenses : revenue).length === 0 && (
                      <div style={{textAlign:'center', padding:'30px 20px', color:'#64748B', fontSize:'12px', fontWeight:'400', display:'flex', flexDirection:'column', alignItems:'center', gap:'12px', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1'}}>
                          <Package size={24} color="#94a3b8"/>
                          No records found.
                      </div>
                  )}
              </div>
          </div>
      </Modal>

      {/* ===== 🌟 PREMIUM REPORT HISTORY MODAL 🌟 ===== */}
      <Modal isOpen={activeModal === 'reports'} onClose={closeModalsVoice} title="Financial Report History" icon={Receipt} iconColor="#4338CA" iconBg="linear-gradient(135deg, #E0E7FF, #A5B4FC)" maxWidth="600px">
          <div>
              <div style={{display:'flex', flexDirection:'column', gap: '10px'}}>
                  {reports.length === 0 && (
                      <div style={{textAlign:'center', padding:'30px 20px', color:'#64748B', fontSize:'12px', fontWeight:'400', display:'flex', flexDirection:'column', alignItems:'center', gap:'12px', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1'}}>
                          <Receipt size={24} color="#94a3b8"/>
                          No history saved.
                      </div>
                  )}
                  {reports.map((h, index) => (
                      <div key={index} onClick={(e) => handleVoiceClick(e, `ये ${formatDate(h.report_date)} को बनाई गई Report है।`, () => generatePDF(JSON.parse(h.config_json), 'view'), 'view-report')} 
                          style={{
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              padding: '14px', 
                              background: '#f8fafc',
                              borderRadius: '16px',
                              border: '1px solid #e2e8f0',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              flexShrink: 0
                          }}>
                          <div style={{display:'flex', flexDirection:'column', gap:'4px', flex:1}}>
                              <span style={{fontSize:'13px', fontWeight:'600', color:'#0F172A'}}>
                                  {formatDate(h.report_date)}
                              </span>
                              <div style={{fontSize:'10px', color:'#64748B', fontWeight:'400', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                  {h.date_range || 'Full Report'}
                              </div>
                          </div>

                          <div style={{
                               background:'#ffffff', 
                               border:'1px solid #e2e8f0', 
                               width:'32px', height:'32px',
                               borderRadius:'10px', 
                               display:'flex', 
                               alignItems:'center', 
                               justifyContent:'center',
                               boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                               transition: 'all 0.2s',
                               }}>
                              <Eye size={14} color="#4338CA"/>
                          </div>
                      </div>
                  ))}
              </div>
          </div>
      </Modal>

      {/* ===== 🌟 PREMIUM DOWNLOAD REPORT MODAL 🌟 ===== */}
      <Modal isOpen={showReportModal} onClose={closeModalsVoice} title=" Download Financial Report" icon={Download} iconColor="#2563EB" iconBg="linear-gradient(135deg, #DBEAFE, #93C5FD)">
          <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                      <label style={{fontSize: '10px', fontWeight: '400', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px', display: 'block', letterSpacing: '0.5px'}}>Report Type</label>
                      <div 
                          onClick={(e) => handleVoiceClick(e, " यह फुल इन्वेंटरी रिपोर्ट है, इसमें खर्च और आमदनी दोनों की पूरी रिपोर्ट मिलेगी।", null)}
                          style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#0F172A', userSelect: 'none', cursor: 'pointer', fontWeight: '400', fontSize: '13px'}}
                      >
                          Full Inventory Report (All)
                      </div>
                  </div>

                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
                      <div>
                          <label style={{fontSize: '10px', fontWeight: '400', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px', display: 'block', letterSpacing: '0.5px'}}>From Date</label>
                          <input 
                              id="input-report-from"
                              type="date" 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                              value={reportConfig.startDate} 
                              onFocus={() => handleFocusVoice(" यहाँ से किस तारीख की Report चाहिए वो तारीख चुनें।")}
                              onChange={e=>setReportConfig({...reportConfig, startDate: e.target.value})} 
                          />
                      </div>
                      <div>
                          <label style={{fontSize: '10px', fontWeight: '400', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px', display: 'block', letterSpacing: '0.5px'}}>To Date</label>
                          <input 
                              id="input-report-to"
                              type="date" 
                              style={{...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0'}} 
                              value={reportConfig.endDate} 
                              onFocus={() => handleFocusVoice(" यहाँ किस तारीख तक की Report निकालनी है, वो तारीख चुनें।")}
                              onChange={e=>setReportConfig({...reportConfig, endDate: e.target.value})} 
                          />
                      </div>
                  </div>
              </div>

              <button id="btn-save-pdf" onClick={(e) => handleVoiceClick(e, " Download Financial Report दबा दिया गया है, आपकी फाइल Download हो रही है।", () => generatePDF(null, 'download'))} 
                  style={{
                      width: '100%', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '24px', transition: 'all 0.2s'
                  }}>
                  <Download size={18} color="white"/> Download Financial Report
              </button>
          </div>
      </Modal>

      {/* ===== 🌟 PREMIUM DELETE CONFIRMATION MODAL 🌟 ===== */}
      <ConfirmModal 
          isOpen={!!deleteConfirmInfo} 
          onClose={() => setDeleteConfirmInfo(null)} 
          onConfirm={(e) => {
              handleVoiceClick(e, " Record Delete कर दिया गया है।", () => deleteEntry(deleteConfirmInfo.id, deleteConfirmInfo.type));
              setDeleteConfirmInfo(null);
          }} 
          message="क्या आप वाकई इस Record को हमेशा के लिए Delete करना चाहते हैं? यह वापस नहीं आएगा।"
          title="Delete Record?"
          isDanger={true}
          confirmText="Delete"
      />

      

         {/* ===== 🌟 PREMIUM DELETE CONFIRMATION MODAL 🌟 ===== */}
      {deleteConfirmInfo && (
          <div style={{...styles.modalOverlay, zIndex: 100005}}> {/* Z-index बढ़ा दिया है ताकि Footer के ऊपर दिखे */}
              <div style={{
                  background: '#ffffff', 
                  borderRadius: '24px', 
                  width: '90%',
                  maxWidth: '320px', 
                  padding: '24px', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
                  border: '1px solid #e2e8f0',
                  animation: 'zoomIn 0.2s ease-out'
              }}>
                  <div style={{
                      background: 'linear-gradient(135deg, #FEE2E2, #FCA5A5)', 
                      width: '64px', height: '64px', 
                      borderRadius: '50%', 
                      display: 'flex', justifyContent: 'center', alignItems: 'center', 
                      marginBottom: '20px',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)'
                  }}>
                      <Trash2 size={28} color="#DC2626" />
                  </div>
                  
                  <h3 style={{fontSize: '18px', fontWeight: '600', color: '#0F172A', margin: '0 0 8px 0', letterSpacing: '-0.3px'}}>
                      Delete Record?
                  </h3>
                  
                  <p style={{fontSize: '12px', color: '#64748B', textAlign: 'center', fontWeight: '600', margin: '0 0 24px 0', lineHeight: '1.6'}}>
                      क्या आप वाकई इस Record को हमेशा के लिए<br/>Delete करना चाहते हैं? यह वापस नहीं आएगा।
                  </p>
                  
                  <div style={{display: 'flex', gap: '12px', width: '100%'}}>
                      <button 
                          onClick={() => setDeleteConfirmInfo(null)}
                          style={{
                              flex: 1, padding: '14px', borderRadius: '16px', border: '1px solid #e2e8f0', 
                              background: '#f8fafc', color: '#475569', fontWeight: '400', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s'
                          }}
                      >
                          Cancel
                      </button>
                      <button 
                          onClick={(e) => {
                              handleVoiceClick(e, " Record Delete कर दिया गया है।", () => deleteEntry(deleteConfirmInfo.id, deleteConfirmInfo.type));
                              setDeleteConfirmInfo(null);
                          }}
                          style={{
                              flex: 1, padding: '14px', borderRadius: '16px', border: 'none', 
                              background: 'linear-gradient(135deg, #EF4444, #DC2626)', color: '#fff', fontWeight: '400', fontSize: '13px', cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)', transition: 'all 0.2s'
                          }}
                      >
                          Delete
                      </button>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
};

const styles = {
 container: { 
      zoom: 0.95,
      fontSize: '14px',
      minHeight: '100vh', 
      paddingBottom: '70px', 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%', 
      overflowY: 'auto', 
      overflowX: 'hidden', 
      fontFamily: "'Plus Jakarta Sans', sans-serif", 
      transition: 'background 0.3s ease', 
      WebkitOverflowScrolling: 'touch'},

  header: { background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '12px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10, willChange: 'transform, backdrop-filter' },
  // maxWidth: 480px को बढ़ाकर 768px (या 800px) कर दिया है ताकि पेज चौड़ा हो जाए
  content: { padding: '16px', maxWidth: '600px', margin: '0 auto' },
  card: { background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderRadius: '24px', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)', border: '1px solid rgba(255, 255, 255, 0.3)' }, 
  
  backBtn: { background: 'rgba(255,255,255,0.4)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', padding: '5px', display:'flex', alignItems:'center' },
  iconBtnSquare: { width:'32px', height:'32px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.4)', background:'rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition: 'all 0.2s' },
  pageTitle: { fontSize: '18px', fontWeight: '400', color: '#111', margin: 0, letterSpacing:'-0.5px' },

  premiumButtonStyle: { 
      width: '100%', background: '#1A1A1A', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '12px', fontWeight: '400', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', transition: 'all 0.25s ease'
  },
  
  
  form: { display: 'flex', flexDirection: 'column' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  
  input: { width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.6)', fontSize: '12px', outline: 'none', background: 'rgba(255,255,255,0.4)', color:'#111', fontWeight:'600', boxSizing: 'border-box', transition: 'all 0.2s', ':focus': {background: 'rgba(255,255,255,0.7)'} },
  fieldLabel: { fontSize: '9px', fontWeight: '400', color: '#444', textTransform: 'uppercase', marginBottom: '8px', display: 'block', letterSpacing: '0.8px' },
  
  tabs: { display: 'flex', background: 'rgba(255,255,255,0.3)', padding: '4px', borderRadius: '16px', marginBottom: '16px' },
  tabActive: { flex: 1, padding: '10px 8px', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '16px', fontSize: '11px', fontWeight: '400', color: '#111', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'all 0.2s' },
  tabInactive: { flex: 1, padding: '10px 8px', background: 'transparent', border: 'none', borderRadius: '16px', fontSize: '11px', fontWeight: '600', color: '#444', cursor: 'pointer' },
  
  modalTabs: { display: 'flex', background: 'rgba(255,255,255,0.4)', padding: '4px', borderRadius: '14px', marginBottom: '16px' },
  modalTabActive: { flex: 1, padding: '8px 6px', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '12px', fontSize: '10px', fontWeight: '400', color: '#111', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'all 0.2s' },
  modalTabInactive: { flex: 1, padding: '8px 6px', background: 'transparent', border: 'none', borderRadius: '12px', fontSize: '10px', fontWeight: '600', color: '#555', cursor: 'pointer' },

  listItem: { display: 'flex', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.2)', alignItems: 'center', contentVisibility: 'auto', containIntrinsicSize: '60px', willChange: 'transform, opacity' },
};

export default Inventory;